/**
 * Every server read and write the app performs, as typed hooks.
 *
 * Screens never call `apiRequest` directly. Keeping the endpoint list here means
 * the cache keys are consistent, and — more importantly — that a mutation's
 * invalidation set is declared next to the mutation. A cost posted without
 * invalidating the work order leaves a screen showing a total that is already
 * wrong, which is the exact class of "the math ain't mathing" this product exists
 * to fix.
 */
import {
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import type {
  AssetOpenWorkRow, GroupedQueueResponse, StageTimeResponse, CollectorSettingsResponse, CreateVendorBody, VendorCandidate, VendorMatchDecisionBody, VendorMatchQueueRow,
  VendorMatchDecidedPair, ReturnableIssueRow, ReturnPartBody,
  CollectorHealthResponse,
  SetPartProductUrlBody, SetPartWarrantyBody, QueueStatusView, SetWorkOrderVendorWarrantyBody, SetWorkOrderVendorWarrantyReceipt, SetWorkOrderCustomFieldsBody,
  SetWorkOrderCustomFieldsReceipt, SetCollectorSettingsBody, SetAssetLifecycleBody,
  AssetLifecycleReceipt, LabourRatesResponse, SetLabourRateBody, AdminCreateUserBody,
  AdminInviteUserBody, AdminInviteResult,
  AdminSetUserAccessBody, AdminUserRow, AddCostBody, AssetClassFilter, AssetRecord, AssetsResponse,
  AssignWorkOrderBody, AssignableParty, BackInServiceBody, BackInServiceResponse, BoardResponse,
  CancelWorkOrderBody,
  CategoryAdminRow, CategoryOption, ClearWorkOrderAttentionBody, CompleteProcedureStepBody,
  ConfirmInvoiceReceipt, ConfigType,
  ConvertRequestBody, CreateAssetBody, CreateLocationBody, CreatePartBody, CreateReasonTypeBody,
  CreateWorkOrderBody, CreateWorkOrderCategoryBody, CreateWorkOrderTypeBody, CreatedWorkOrderType,
  DeclineRequestBody, DetachDocumentBody, VerifyDocumentBody, DriverContext, DriverRequestBody,
  EditDueDateBody, EditStageBody, Envelope, FaultTypeAdminRow, FaultTypeReceipt,
  InboxItem, IssuePartBody, LocationOption,
  LogRequestBody, LogTimeBody, MarkAssetDownBody, OverviewResponse, Paged, PartRow,
  PostCommentBody, EraseCommentBody, PurchaseOrderRow, QueueResponse, ReceivePartBody, RecordPurchaseReceiptBody,
  ReleaseDraftBody, ReportsResponse, RequestDetail, RequestInboxResponse, RequestsInboxQuery,
  ResumeStoppageBody, ReverseCostBody, ReviewDetail, ReviewLineDecisionBody,
  ReviewLineDecisionReceipt, SessionResponse, SetCostStatusBody, SetWorkOrderFaultsBody,
  SetRootCauseBody, SetRoutinePermissionsBody, SetRoutinePermissionsReceipt, SetVendorStatusBody,
  PurchaseOrderDetail, PurchaseOrderStatusBody, SavePurchaseOrderBody, SetWorkOrderCategoriesBody,
  SetWorkOrderCategoriesReceipt, SetWorkOrderMeterReadingBody, SetWorkOrderVendorBody,
  AddWorkOrderVendorBody, AddWorkOrderVendorReceipt, RemoveWorkOrderVendorBody,
  RemoveWorkOrderVendorReceipt,
  SettingsPermissionsResponse, StageEditReceipt, StartStoppageBody, StartStoppageReceipt,
  StoppagesResponse, TransitionBody, TransitionResult, UnassignWorkOrderBody,
  UpdateReasonTypeBody, UpdateWorkOrderCategoryBody, VendorProfile, VendorRow, VendorSuggestion,
  WorkOrderCategoryReceipt, WorkOrderDetail, DocumentScanHealthResponse, NearbyVendors,
  VendorAddressBody, VendorProfileBody, KanbanBoardQuery, KanbanBoardQueryParams, KanbanBoardResponse,
  LivePositionsResponse, MapsConfigResponse, PositionRefreshReceipt,
  ProcedureAdminRow, ProcedureReceipt, CreateProcedureBody,
  AssetAttachmentsResponse, WorkOrderAttachmentsResponse, CorrectDowntimeBody, CorrectTimeBody,
  VoidTimeBody, VoidCostBody,
  CreateManualInvoiceBody, CreatePurchaseOrderBillBody, ConfirmInvoiceBody,
  ManualInvoiceReceipt, AiProposal, ReceiptParserState, ReceiptReadingModeSaved,
  ReviewQueueTab, ReviewQueueCounts, SetReceiptReadingModeBody,
  CreateHomeBaseMapBody, UpdateHomeBaseMapBody, HomeBaseMapAdminRow, HomeBaseMapReceipt,
  WorkOrderRequirementsResponse,
  RecordVendorPaymentBody, VendorPaymentReceipt, VendorPaymentsView,
  PartMovementRow, ReverseMovementBody, ReverseMovementReceipt,
  PartStockLocation, PartSummary, TransferStockBody, TransferStockReceipt,
  RecordStockCountBody, StockCountReceipt,
  SetPartUnitCostBody, RecordWorkOrderLoadStatusBody,
  CloseImportedWorkOrderBody, CloseImportedWorkOrderResult,
  CloseImportedWorkOrdersBody, CloseImportedBulkResult,
  KanbanQuery, KanbanQueryParams, KanbanCellResponse,
  WorkOrderTimelineQuery, WorkOrderTimelineResponse,
  SetPreviewRoleBody, SetAssetClassBody, AssetClassReceipt,
  AssetProfileBody, AssetProfileReceipt, AssetFieldOptions } from "@truckbuddy/contract";
import { POSITION_TTL_MINUTES } from "@truckbuddy/contract";
/* The counts on the menu — TRBUD-443. */
import { hasCountedNavigation, type NavCounts } from "@truckbuddy/contract";
import type {
  AddWorkOrderIssuesBody,
  CreateWorkPackageBody,
  CreateWorkPackageResult,
  UpdateWorkOrderIssueBody,
} from "@truckbuddy/contract";
/* The board's manager actions — 18 September 2026. */
import type {
  FlagWorkOrderAttentionBody,
  Priority,
  QueueAttention,
  SetWorkOrderPriorityBody,
  SetWorkOrderTitleBody,
} from "@truckbuddy/contract";
/* "No parts used" on an in-house work order — TRBUD-300. */
import type {
  ClearNoPartsUsedBody,
  MarkNoPartsUsedBody,
  NoPartsUsedReceipt,
} from "@truckbuddy/contract";
/* Files on a purchase order, and their part photos — TRBUD-351. */
import type {
  AddPurchaseOrderPartPhotoBody,
  AddPurchaseOrderPartPhotoReceipt,
  DetachPurchaseOrderDocumentReceipt,
} from "@truckbuddy/contract";
// TRBUD-466: vendor data completeness and adding or editing a vendor's contacts.
import type {
  CreateVendorContactBody,
  EditVendorContactBody,
  VendorCompletenessField,
  VendorCompletenessResponse,
} from "@truckbuddy/contract";
import { apiRequest, newIdempotencyKey } from "./api";

/** One namespace for cache keys, so an invalidation cannot miss by a typo. */
export const keys = {
  session: ["session"] as const,
  queue: (params: unknown) => ["queue", params] as const,
  queueGrouped: (params: unknown) => ["queue", "grouped", params] as const,
  workOrder: (id: string) => ["work-order", id] as const,
  assets: (params: unknown) => ["assets", params] as const,
  asset: (id: string) => ["asset", id] as const,
  /**
   * Not `["assets", ...]` — a DELIBERATE separate namespace, not a tidiness
   * choice. `useInvalidate` invalidates `["assets"]` as a prefix after every
   * downtime write, and folding positions under it would make marking an asset
   * down re-fetch the map (harmless) and, worse, make the map's own five-minute
   * refetch look like part of the asset list's cache. They are separate reads
   * behind separate capabilities and they invalidate for different reasons.
   */
  livePositions: ["live-positions"] as const,
  mapsConfig: ["maps-config"] as const,
  assetOpenWork: (id: string) => ["asset", id, "open-work"] as const,
  vendors: (params: unknown) => ["vendors", params] as const,
  vendorCandidates: (params: unknown) => ["vendors", "candidates", params] as const,
  /** Under `["vendors"]`, so every vendor write that refreshes the list refreshes this too. */
  vendorCompleteness: (includeRetired: boolean) => ["vendors", "completeness", includeRetired] as const,
  vendorMatches: ["settings", "vendor-matches"] as const,
  vendorMatchDecisions: ["settings", "vendor-match-decisions"] as const,
  returnableIssues: (partId: string) => ["parts", partId, "returnable-issues"] as const,
  purchaseOrder: (id: string) => ["purchase-order", id] as const,
  requestInbox: (params: unknown) => ["request-inbox", params] as const,
  requestDetail: (id: string) => ["request-detail", id] as const,
  vendor: (id: string) => ["vendor", id] as const,
  parts: (params: unknown) => ["parts", params] as const,
  locations: ["locations"] as const,
  purchaseOrders: ["purchase-orders"] as const,
  reports: (timeframe: string) => ["reports", timeframe] as const,
  overview: ["overview"] as const,
  board: ["board"] as const,
  requests: ["requests"] as const,
  review: ["review"] as const,
  /** Every tab and page of the queue list: invalidate this to refresh whichever is open. */
  reviewLists: ["review", "list"] as const,
  reviewList: (tab: string, offset: number, search = "") => ["review", "list", tab, offset, search] as const,
  reviewItem: (id: string) => ["review", id] as const,
  workOrderTypes: ["work-order-types"] as const,
  config: ["config"] as const,
  rootCauses: ["root-causes"] as const,
  reasonTypes: ["reason-types"] as const,
  /** Shim key — folds into `workOrder(id)` when `WorkOrderDetail` carries the field. */
  assignableParties: ["assignable-parties"] as const,
  settingsPermissions: ["settings", "permissions"] as const,
  collectorSettings: ["settings", "collectors"] as const,
  labourRates: ["settings", "labour-rates"] as const,
  collectorHealth: ["settings", "collectors", "health"] as const,
  documentScanHealth: ["settings", "documents", "scan-health"] as const,
  adminUsers: ["admin", "users"] as const,
  /**
   * Categories come in three keys because there are three different questions,
   * and one key would make the /config screen's edits invalidate a picker that
   * shows a different set. `categoryOptions` is active-only; `configCategories`
   * includes the deactivated ones.
   */
  categoryOptions: ["categories"] as const,
  configCategories: ["config", "categories"] as const,
  /**
   * Fault types for the /config screen — the admin half of `reasonTypes`.
   *
   * Two keys for the same reason categories have two: `reasonTypes` is the
   * picker's list and is ACTIVE-ONLY (`GET /reason-types` filters on it), this
   * one includes what has been retired. An edit has to invalidate BOTH, because
   * a rename shows up in the picker and a deactivation has to LEAVE it while
   * staying visible here. See `useInvalidateFaultTypes`.
   */
  configFaultTypes: ["config", "fault-types"] as const,
  /**
   * Procedures with their steps, for the /config editor (TRBUD-170).
   *
   * No picker counterpart to keep in step, unlike `configCategories`: the only
   * other place procedures are read is a work order's own detail, which fetches
   * them inside `WorkOrderDetail` rather than from a list of its own.
   */
  configProcedures: ["config", "procedures"] as const,
  /**
   * The Sylectus home-base mapping (TRBUD-171).
   *
   * No picker counterpart either: this table is consulted by a batch job
   * (`npm run project:home-base`), never by a form, so there is no active-only
   * variant to keep in step.
   */
  configHomeBaseMap: ["config", "home-base-map"] as const,
  workOrderCategories: (id: string) => ["work-order", id, "categories"] as const,
  /**
   * The exit gate for the stage this work order is standing in (TRBUD-156).
   *
   * A prefix extension of `keys.workOrder(id)`, which is what makes it correct
   * without any mutation having to know it exists: TanStack Query invalidates by
   * prefix, and `invalidate.workOrder` is already called by every write that
   * could satisfy a requirement — assigning, logging time, posting a part cost,
   * setting the fault type, verifying a document. A sibling top-level key would
   * have left the panel asserting "missing" beside the record that satisfied it.
   */
  workOrderRequirements: (id: string) => ["work-order", id, "requirements"] as const,
  stoppages: (id: string) => ["work-order", id, "stoppages"] as const,
  stageTime: (id: string) => ["work-order", id, "stage-time"] as const,
  workOrderDocuments: (id: string) => ["work-order", id, "documents"] as const,
  /**
   * Nested UNDER `asset(id)` on purpose — TRBUD-151.
   *
   * `invalidate.asset` matches by key prefix, so a detach, a photo delete or a
   * downtime correction refreshes this list without any of those mutations
   * having to remember it exists. The alternative (a sibling top-level key)
   * is how a card ends up still showing a document nobody can open.
   */
  assetDocuments: (id: string) => ["asset", id, "documents"] as const,
  /** Separate from `session`: it survives a sign-in and is not the payload. */
  sessionState: ["session-state"] as const,
  /**
   * The menu's counts (TRBUD-443). Its own namespace, invalidated by name by the
   * writes that change one of the three figures (`invalidateNavCounts`): it
   * holds request, review and attendance figures at once, so nesting it under
   * any one of those keys would leave the other two stale.
   */
  navCounts: ["nav-counts"] as const,
};

/**
 * Refresh the menu's counts after a write that can change one. Writes that
 * already call `invalidate.everything()` (log, convert or decline a request;
 * log, confirm or mark an invoice) need not call this.
 */
export function invalidateNavCounts(client: Pick<QueryClient, "invalidateQueries">): void {
  void client.invalidateQueries({ queryKey: keys.navCounts });
}

/* ── reads ───────────────────────────────────────────────────────── */

/**
 * Is there a live session at all? -- the sign-in page's probe.
 *
 * `/api/session` answers 401 without a cookie, which is correct and is a
 * documented invariant, but it means every visit to the sign-in page logs a
 * failed request (two Lighthouse runs scored best-practices 96 for it). This
 * asks `/auth/session-state`, which is outside the `/api` scope and answers 200
 * either way, carrying one boolean and nothing else.
 *
 * `apiRequest` takes a path under `/api`, so the absolute URL is spelled here.
 */
export function useSessionState(): UseQueryResult<
  { authenticated: boolean; passwordResetAvailable?: boolean },
  Error
> {
  return useQuery({
    queryKey: keys.sessionState,
    queryFn: async () => {
      const response = await fetch("/auth/session-state", { credentials: "same-origin" });
      if (!response.ok) {
        // A failing probe must not strand the app on a spinner. Assume there IS
        // a session and let `/api/session` give the real answer -- which is
        // exactly the behaviour before this probe existed.
        return { authenticated: true };
      }
      return (await response.json()) as { authenticated: boolean; passwordResetAvailable?: boolean };
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useSession(enabled = true): UseQueryResult<SessionResponse, Error> {
  return useQuery({
    queryKey: keys.session,
    queryFn: () => apiRequest<SessionResponse>("/session"),
    // Gated on the probe, so an unauthenticated visitor never sends a request
    // that is certain to 401. Defaults to true: every other caller of this hook
    // runs inside the shell, which only renders once a session is known.
    enabled,
    // The session decides which chrome renders at all; refetching it on every
    // window focus would flicker the whole shell.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export interface QueueParams {
  /** Narrow pickers to jobs the caller may change. */
  writable?: boolean;
  q?: string;
  stageId?: string;
  typeId?: string;
  repairKind?: "INTERNAL" | "VENDOR";
  /**
   * TRBUD-441: one vendor, and the work-category list as the COMMA-SEPARATED
   * string the URL carries (`queueQuery.categoryIds` says why it is not an
   * array: `buildUrl` drops an array without a word).
   */
  vendorId?: string;
  categoryIds?: string;
  priority?: string;
  overdueOnly?: boolean;
  /**
   * Which view of the queue — TRBUD-216.
   *
   * `QueueStatusView`, not a local union, so a value the API would refuse cannot
   * be written here. Omit it for the default (`open`); the queue screen omits it
   * for the Open tab rather than sending `status=open`, on the same reasoning as
   * `includeCancelled` below.
   *
   * **The four work-order PICKERS pass `"unclosed"`.** `open` stopped meaning
   * "not closed" and started meaning "not finished", and a vendor invoice arrives
   * after the repair is complete — so a picker on the default would silently lose
   * the work orders that most need a cost attached. They say what they need
   * instead of inheriting it.
   */
  status?: QueueStatusView;
  /** Due on or before this `YYYY-MM-DD`, inclusive. Undated work never matches. */
  dueBefore?: string;
  /**
   * Show work orders that have been deleted (soft-deleted into `cancelled`).
   *
   * Absent means false, and callers pass `undefined` rather than `false` for it
   * on purpose: `queueQuery` in the contract is `.strict()`, so this key reaching
   * an API that does not yet know it is a 400 for the entire queue rather than an
   * ignored parameter. Omitting it keeps the default request identical to what
   * shipped before the toggle existed.
   *
   * The queue screen no longer sends it — the Cancelled tab (`status`) is the
   * affordance now, so there is one control for one job. It stays here as the API
   * overlay it always was: "open work AND the cancelled ones", which no single
   * `status` value expresses.
   */
  includeCancelled?: boolean;
  /** TRBUD-401: the "Awaiting final cost" set, from `?costStatus=awaiting_final_cost`. */
  costStatus?: "awaiting_final_cost";
  limit?: number;
  offset?: number;
}

export function useQueue(params: QueueParams = {}): UseQueryResult<Envelope<QueueResponse>, Error> {
  return useQuery({
    queryKey: keys.queue(params),
    queryFn: () => apiRequest<Envelope<QueueResponse>>("/work-orders", { query: params }),
    // Keep the previous rows on screen while a filter change loads. Without this
    // the list unmounts on every keystroke, which takes the search input with it
    // and drops focus mid-word.
    placeholderData: keepPreviousData,
  });
}

/**
 * The whole board in one request -- rider 4.4.
 *
 * The board has sixteen cells and `useKanbanCell` fetches one, so mounting the
 * overview fired sixteen requests and repeated them every 60 seconds. This asks
 * once. `useKanbanCell` stays for the one thing a board request cannot answer: a
 * cell paged past its first page.
 */
export function useKanbanBoard(params: KanbanBoardQuery | KanbanBoardQueryParams, options: { enabled?: boolean } = {}) {
  const query = { ...params, categoryIds: Array.isArray(params.categoryIds) ? params.categoryIds.join(",") : params.categoryIds };
  return useQuery({
    queryKey: ["queue", "kanban-board", query],
    queryFn: () =>
      apiRequest<Envelope<KanbanBoardResponse>>("/work-orders/kanban/board", { query }),
    refetchInterval: 60_000,
    enabled: options.enabled ?? true,
  });
}

/**
 * ONE cell, for a cell that has been paged past its first page.
 *
 * `enabled` is how the board and this stay out of each other's way: a cell at
 * offset 0 reads its rows from the board response and runs no query of its own,
 * so the sixteen-request mount cannot come back by accident.
 */
export function useKanbanCell(params: KanbanQuery | KanbanQueryParams, options: { enabled?: boolean } = {}) {
  const query = { ...params, categoryIds: Array.isArray(params.categoryIds) ? params.categoryIds.join(",") : params.categoryIds };
  return useQuery({
    queryKey: ["queue", "kanban", query],
    queryFn: () => apiRequest<Envelope<KanbanCellResponse>>("/work-orders/kanban", { query }),
    refetchInterval: 60_000,
    enabled: options.enabled ?? true,
  });
}

/**
 * The Gantt lane under the board — 18 September 2026.
 *
 * `refetchInterval` matches `useKanbanBoard`'s sixty seconds on purpose: the two
 * are one screen describing one set of work, and a chart refreshing on a
 * different cadence from the columns above it would show a bar for a card that
 * is no longer there (or the other way round) for as long as the two intervals
 * disagree. One number, and a reader comparing them sees one moment.
 */
export function useWorkOrderTimeline(
  params: WorkOrderTimelineQuery,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ["queue", "timeline", params],
    queryFn: () =>
      apiRequest<Envelope<WorkOrderTimelineResponse>>("/work-orders/timeline", { query: params }),
    refetchInterval: 60_000,
    enabled: options.enabled ?? true,
  });
}

export function useWorkOrder(id: string | undefined) {
  return useQuery({
    queryKey: keys.workOrder(id ?? ""),
    queryFn: () => apiRequest<Envelope<WorkOrderDetail>>(`/work-orders/${id}`),
    enabled: Boolean(id),
  });
}

export interface AssetParams {
  q?: string;
  assetType?: string;
  location?: string;
  status?: "in_service" | "down" | "testing";
  /** TRBUD-401: the "Assets down" figure's own set, from `?availability=down`. */
  availability?: "down";
  /** The Trucks / Trailers tab. Omitted means every class — the All tab. */
  assetClass?: AssetClassFilter;
  limit?: number;
  offset?: number;
}

/**
 * `enabled` matters here: several dialogs load a 200-asset picker that is not
 * rendered when the asset is already prefilled from a row. Fetching it anyway is
 * a wasted round trip on every dialog open.
 */
export function useAssets(params: AssetParams = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.assets(params),
    queryFn: () => apiRequest<Envelope<AssetsResponse>>("/assets", { query: params }),
    // Keep the previous rows on screen while a filter change loads. Without this
    // the list unmounts on every keystroke, which takes the search input with it
    // and drops focus mid-word.
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}

/**
 * The fleet's live positions — TRBUD-55.
 *
 * `enabled` is the caller's, and every caller passes `can.viewLivePositions`.
 * Four of six roles hold that, so firing this unconditionally would put a
 * guaranteed 403 in a mechanic's console on every visit to the Assets screen,
 * and TanStack would keep the failed query in the cache for the session.
 *
 * `refetchInterval` matches the server's own TTL rather than being tuned
 * separately: the read never calls Samsara, so this only re-reads the cache, and
 * polling faster than the cache can change would be work for an identical
 * answer. Slower would mean markers rendering as `live` after the server had
 * begun calling them stale.
 */
export function useLivePositions(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.livePositions,
    queryFn: () => apiRequest<Envelope<LivePositionsResponse>>("/assets/positions"),
    enabled: options.enabled ?? true,
    refetchInterval: POSITION_TTL_MINUTES * 60_000,
  });
}

/**
 * The Azure Maps browser key, fetched at runtime rather than bundled.
 *
 * `staleTime` is generous because the answer changes only when somebody
 * provisions a Maps account or rotates the key, and a key rotation already
 * requires a new session to matter. Copying QuoteBuddy's five minutes.
 */
export function useMapsConfig(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.mapsConfig,
    queryFn: () => apiRequest<MapsConfigResponse>("/maps/config"),
    enabled: options.enabled ?? true,
    staleTime: 5 * 60_000,
  });
}

/**
 * Ask Samsara now.
 *
 * `retry: false`, like every other mutation here, and it matters more than usual:
 * the server's cross-replica lease refuses a second poll inside the interval, so
 * an automatic retry would turn one refusal into two and report the retry's
 * `poll rate limited` instead of the original reason.
 *
 * Invalidating on settle rather than on success, because a refusal is still
 * news — `lastPolledAt` and `lastPollStatus` move on a FAILED poll, and the
 * panel reads them.
 */
export function useRefreshPositions(): UseMutationResult<
  Envelope<PositionRefreshReceipt>,
  Error,
  void
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<Envelope<PositionRefreshReceipt>>("/assets/positions/refresh", {
        method: "POST",
        body: {},
      }),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.livePositions });
    },
    retry: false,
  });
}

export function useAsset(id: string | undefined) {
  return useQuery({
    queryKey: keys.asset(id ?? ""),
    queryFn: () => apiRequest<Envelope<AssetRecord>>(`/assets/${id}`),
    enabled: Boolean(id),
  });
}

/**
 * The grouped queue's own parameters — `QueueParams` plus `mine`.
 *
 * A separate type rather than a key on `QueueParams`, because `useQueue` shares
 * that type with the four work-order pickers and `mine` must not be reachable
 * from them: a picker filtered to "my work" would hide the work order somebody
 * is trying to name against a cost or a stoppage. `queueQuery.mine` is
 * `.optional()` with no server default for the same reason, and this keeps the
 * client half of that promise at the type level rather than by convention.
 */
export interface GroupedQueueParams extends QueueParams {
  /**
   * "Waiting on me", resolved by the SERVER against the caller's role — assigned
   * to me for a mechanic, waiting on a decision for the management roles,
   * ignored for `requester` and `viewer`. Never send a user id for it.
   *
   * Pass it only when TRUE: a present-and-false value costs the API a second
   * full scan for the "n of N assets" denominator and buys nothing, which
   * `readQueueGrouped`'s exemption list spells out.
   */
  mine?: boolean;
  /**
   * The column order, `lastUpdate:desc,due:asc` (TRBUD-391): tiers in click
   * order. A string, like `categoryIds`, because `buildUrl` drops an array
   * without a word. Pass it only while an order is on; absent is the default
   * order, and with it the answer is `ordered` rows instead of asset `groups`.
   */
  sort?: string;
}

export function useQueueGrouped(params: GroupedQueueParams = {}): UseQueryResult<Envelope<GroupedQueueResponse>, Error> {
  return useQuery({
    queryKey: keys.queueGrouped(params),
    queryFn: () => apiRequest<Envelope<GroupedQueueResponse>>("/work-orders/grouped", { query: params }),
    placeholderData: keepPreviousData,
  });
}

export function useAssetOpenWork(id: string | undefined) {
  return useQuery({
    queryKey: keys.assetOpenWork(id ?? ""),
    queryFn: () => apiRequest<Envelope<AssetOpenWorkRow[]>>(`/assets/${id}/open-work`),
    enabled: Boolean(id),
  });
}

export interface VendorParams {
  q?: string;
  vendorType?: "repair" | "parts" | "external";
  status?: string;
  capability?: string;
  minRating?: number;
  /** The location filter and sort — TRBUD-82. Real columns, not overlay values. */
  city?: string;
  region?: string;
  sort?: "name" | "location";
  /** TRBUD-466: only the vendors missing this field, from the completeness page. */
  missing?: VendorCompletenessField;
  /** With `missing` only: count inactive and blacklisted vendors too. */
  includeRetired?: true;
}

/**
 * `enabled` — TRBUD-441. `GET /vendors` needs `viewVendors`, which a viewer and
 * a requester lack, so a screen they share (the work-order queue's vendor
 * filter) passes `can.viewVendors` here rather than sending a request certain to
 * answer 403 and printing "Could not load vendors" beside a list that loaded.
 */
export function useVendors(params: VendorParams = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.vendors(params),
    queryFn: () =>
      apiRequest<Envelope<Paged<VendorRow>>>("/vendors", { query: params }),
    // Keep the previous rows on screen while a filter change loads. Without this
    // the list unmounts on every keystroke, which takes the search input with it
    // and drops focus mid-word.
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}

/**
 * How complete the vendor records are, field by field — TRBUD-466.
 *
 * `enabled` follows `manageVendors`, the route's own gate, so a role that would
 * only get a 403 sends nothing.
 */
export function useVendorCompleteness(includeRetired: boolean, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.vendorCompleteness(includeRetired),
    queryFn: () =>
      apiRequest<Envelope<VendorCompletenessResponse>>("/vendors/completeness", {
        query: includeRetired ? { includeRetired: true } : {},
      }),
    enabled: options.enabled ?? true,
  });
}

export function useVendorCandidates(
  params: { name: string; phone?: string; email?: string; website?: string },
) {
  return useQuery({
    queryKey: keys.vendorCandidates(params),
    queryFn: () => apiRequest<{ data: VendorCandidate[] }>("/vendors/candidates", { query: params }),
    enabled: params.name.trim().length >= 3,
  });
}

export function useCreateVendor() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateVendorBody) =>
      apiRequest<{ data: { vendorId: string } }>("/vendors", { method: "POST", body }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["vendors"] }),
    retry: false,
  });
}

export function useVendorMatches(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.vendorMatches,
    queryFn: () => apiRequest<{ data: VendorMatchQueueRow[] }>("/settings/vendor-matches"),
    enabled: options.enabled ?? true,
    staleTime: 0,
  });
}

/**
 * The pairs already ruled on — TRBUD-107 / F-23.
 *
 * `staleTime: 0` like the queue beside it. A ruling recorded on another tab
 * changes what this list says about the same pair, and a cached "current
 * verdict" is precisely the wrong thing to show somebody deciding whether to
 * overturn it.
 */
export function useVendorMatchDecisions(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.vendorMatchDecisions,
    queryFn: () =>
      apiRequest<Envelope<VendorMatchDecidedPair[]>>("/settings/vendor-matches/decisions"),
    enabled: options.enabled ?? true,
    staleTime: 0,
  });
}

export function useRecordVendorMatchDecision() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: VendorMatchDecisionBody) =>
      apiRequest<{ data: { decisionId: string; superseding: boolean } }>(
        "/settings/vendor-matches/decision",
        { method: "POST", body },
      ),
    // BOTH lists, because one ruling moves a pair between them: a first ruling
    // takes it out of the queue and into the decided list, and a superseding one
    // changes the verdict shown in the decided list. Invalidating only the queue
    // — which is what this did while nothing read the rulings — would leave the
    // decided list showing the verdict that was just overturned.
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.vendorMatches });
      void client.invalidateQueries({ queryKey: keys.vendorMatchDecisions });
    },
    retry: false,
  });
}

export function useVendor(id: string | undefined) {
  return useQuery({
    queryKey: keys.vendor(id ?? ""),
    queryFn: () =>
      apiRequest<Envelope<{ profile: VendorProfile; suggestion: VendorSuggestion }>>(
        `/vendors/${id}`,
      ),
    enabled: Boolean(id),
  });
}

export interface PartParams {
  q?: string;
  location?: string;
  flaggedOnly?: boolean;
}

export function useParts(params: PartParams = {}) {
  return useQuery({
    queryKey: keys.parts(params),
    queryFn: () =>
      apiRequest<Envelope<Paged<PartRow>>>("/parts", { query: params }),
    // Keep the previous rows on screen while a filter change loads. Without this
    // the list unmounts on every keystroke, which takes the search input with it
    // and drops focus mid-word.
    placeholderData: keepPreviousData,
  });
}

/** One purchase order with its lines — the draft the dialog reopens. */
export function usePurchaseOrder(id: string | undefined) {
  return useQuery({
    queryKey: keys.purchaseOrder(id ?? ""),
    queryFn: () =>
      apiRequest<Envelope<PurchaseOrderDetail>>(`/purchase-orders/${id}`),
    enabled: id !== undefined,
  });
}

function useInvalidatePurchaseOrders() {
  const client = useQueryClient();
  return (id?: string) => {
    void client.invalidateQueries({ queryKey: ["purchase-orders"] });
    void client.invalidateQueries({ queryKey: ["parts"] });
    if (id) void client.invalidateQueries({ queryKey: keys.purchaseOrder(id) });
  };
}

/** Create a draft. Returns the id the dialog then edits against. */
export function useCreatePurchaseOrder() {
  const invalidate = useInvalidatePurchaseOrders();
  return useMutation({
    mutationFn: (body: SavePurchaseOrderBody) =>
      apiRequest<{ poId: string; displayId: string }>("/purchase-orders", {
        method: "POST",
        body,
      }),
    onSettled: (result) => invalidate(result?.poId),
    retry: false,
  });
}

/** Replace a draft, lines and all. */
export function useSavePurchaseOrder() {
  const invalidate = useInvalidatePurchaseOrders();
  return useMutation({
    mutationFn: ({ poId, body }: { poId: string; body: SavePurchaseOrderBody }) =>
      apiRequest<{ poId: string }>(`/purchase-orders/${poId}`, { method: "PUT", body }),
    onSettled: (_result, _error, variables) => invalidate(variables.poId),
    retry: false,
  });
}

/** Submit or cancel. NOT approval — see `purchaseOrderStatusBody`. */
export function useSetPurchaseOrderStatus() {
  const invalidate = useInvalidatePurchaseOrders();
  return useMutation({
    mutationFn: ({ poId, body }: { poId: string; body: PurchaseOrderStatusBody }) =>
      apiRequest<{ poId: string; status: string }>(`/purchase-orders/${poId}/status`, {
        method: "PATCH",
        body,
      }),
    onSettled: (_result, _error, variables) => invalidate(variables.poId),
    retry: false,
  });
}

export function useReceivePurchaseOrder() {
  const invalidate = useInvalidatePurchaseOrders();
  return useMutation({
    mutationFn: ({ poId, body }: { poId: string; body: RecordPurchaseReceiptBody }) =>
      apiRequest<{ poId: string; receiptId: string }>(`/purchase-orders/${poId}/receipts`, {
        method: "POST",
        body,
      }),
    onSettled: (_result, _error, variables) => invalidate(variables.poId),
    retry: false,
  });
}

/**
 * Put a photo that is on a purchase order onto one of its parts — TRBUD-351.
 * Refreshes the order (the file's "On ..." list) and the part's photos.
 */
export function useAddPurchaseOrderPartPhoto(poId: string) {
  const client = useQueryClient();
  const invalidate = useInvalidatePurchaseOrders();
  return useMutation({
    mutationFn: (body: AddPurchaseOrderPartPhotoBody) =>
      apiRequest<AddPurchaseOrderPartPhotoReceipt>(`/purchase-orders/${poId}/part-photos`, {
        method: "POST",
        body,
      }),
    onSettled: (_result, _error, body) => {
      invalidate(poId);
      void client.invalidateQueries({ queryKey: ["parts", body.partId] });
    },
    retry: false,
  });
}

/**
 * After a file is uploaded to a purchase order (`FileUpload`, which does its
 * own request): the order's file list, and the Review queue a receipt joins.
 */
export function usePurchaseOrderFileUploaded(poId: string) {
  const client = useQueryClient();
  const invalidate = useInvalidatePurchaseOrders();
  return () => {
    invalidate(poId);
    void client.invalidateQueries({ queryKey: keys.reviewLists });
    invalidateNavCounts(client);
  };
}

/**
 * Take a file off a purchase order, with a reason (TRBUD-375). The order (its
 * file list and its Removed files), the order list's receipt badge, the parts a
 * photo may sit on, and the Review queue an Unaudited receipt leaves.
 */
export function useDetachPurchaseOrderDocument(poId: string) {
  const client = useQueryClient();
  const invalidate = useInvalidatePurchaseOrders();
  return useMutation({
    mutationFn: (input: { documentId: string; body: DetachDocumentBody }) =>
      apiRequest<DetachPurchaseOrderDocumentReceipt>(
        `/purchase-orders/${poId}/documents/${input.documentId}/detach`,
        { method: "POST", body: input.body },
      ),
    onSettled: () => {
      invalidate(poId);
      void client.invalidateQueries({ queryKey: keys.review });
      invalidateNavCounts(client);
    },
    retry: false,
  });
}

export function usePurchaseOrders() {
  return useQuery({
    queryKey: keys.purchaseOrders,
    queryFn: () => apiRequest<Envelope<PurchaseOrderRow[]>>("/purchase-orders"),
  });
}

export function useReports(timeframe: "all" | "30d" | "quarter" | "ytd", range?: { start: string; end: string }, filters: { jobCategoryId?: string; vendorId?: string } = {}, enabled = true) {
  return useQuery({
    enabled,
    queryKey: [...keys.reports(timeframe), range ?? null, filters],
    queryFn: () => apiRequest<Envelope<ReportsResponse>>("/reports", { query: { timeframe, ...range, ...filters } }),
  });
}

/** How often the menu's counts are read again while the tab is in front (TRBUD-443). */
export const NAV_COUNTS_POLL_MS = 60_000;

/**
 * The counts on the menu for Requests, Review queue and Attendance review —
 * TRBUD-443.
 *
 * Called ONCE, by the shell, before any of its early returns, and handed to the
 * desktop menu and the phone bar; both drawing from one query is what keeps the
 * rail and the phone from disagreeing.
 *
 * - Enabled only for a signed-in person whose menu holds a counted entry
 *   (`hasCountedNavigation`, read from the same table the menu draws), with no
 *   password change owed and not locked out by a maintenance window, so a
 *   mechanic's or a viewer's browser never asks.
 * - Every 60 seconds while the tab is in front, again on focus, never in a
 *   background tab (`refetchIntervalInBackground: false`). Nothing typed into a
 *   page triggers it; the writes that change a figure invalidate it.
 * - Answers null until loaded and after a failed read, so the menu draws nothing
 *   rather than a stale or invented number (cold start: nothing is blocked).
 */
export function useNavCounts(session: SessionResponse | undefined, lockedOut: boolean): NavCounts | null {
  const enabled = session !== undefined && !session.mustChangePassword && !lockedOut &&
    hasCountedNavigation(session.roles, session.can);
  const query = useQuery({
    queryKey: [...keys.navCounts, session?.user.userId ?? null, session?.previewRole ?? null],
    queryFn: () => apiRequest<Envelope<NavCounts>>("/nav-counts"),
    enabled,
    refetchInterval: NAV_COUNTS_POLL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
  if (!enabled || query.status !== "success") return null;
  return query.data.data;
}

export function useOverview() {
  return useQuery({
    queryKey: keys.overview,
    queryFn: () => apiRequest<Envelope<OverviewResponse>>("/overview"),
  });
}

export function useBoard() {
  return useQuery({
    queryKey: keys.board,
    queryFn: () => apiRequest<Envelope<BoardResponse>>("/board"),
    // The board is a wall display. It refreshes on its own, and the freshness
    // stamps in the footer say how old the underlying vendor reads are — so a
    // stale source still reads as stale even on a screen that keeps updating.
    refetchInterval: 60_000,
  });
}

export function useDriverContext() {
  return useQuery({
    queryKey: keys.requests,
    queryFn: () => apiRequest<Envelope<DriverContext>>("/requests"),
  });
}

/** What `GET /review?tab=` answers with. */
export interface ReviewInboxPage {
  items: InboxItem[];
  mailbox: string;
  awaitingEntryCount: number;
  /** Always sent by the server; null only from an old fixture. */
  receiptParser: ReceiptParserState | null;
  tab?: ReviewQueueTab;
  /** Every tab's matching count, from the same snapshot as the rows. */
  counts?: ReviewQueueCounts;
  /** The next Posted page's offset, or null when this is the last. */
  nextOffset?: number | null;
}

/**
 * One tab of the review queue (Unaudited, Logged or Posted), filtered by the
 * server. `offset` pages Posted; the two worklists are never paged.
 */
export function useReviewInbox(tab: ReviewQueueTab = "unaudited", offset = 0, search = "") {
  return useQuery({
    queryKey: keys.reviewList(tab, offset, search),
    // `awaitingEntryCount` — TRBUD-245. Counted server-side from the same rows
    // the whole queue, independent of the metadata search.
    queryFn: () =>
      apiRequest<Envelope<ReviewInboxPage>>(
        `/review?tab=${tab}${offset > 0 ? `&offset=${offset}` : ""}${search ? `&search=${encodeURIComponent(search)}` : ""}`,
      ),
    // While the AI is still reading something, look again: an upload's parse
    // runs after the upload answers, so nothing else would tell the list.
    refetchInterval: (query) =>
      query.state.data?.data.items.some((item) => item.aiProposal?.status === "pending")
        ? AI_PENDING_POLL_MS
        : false,
  });
}

/**
 * The reading-mode switch on the Review header: off, manual or auto. Carries
 * the version the page read, so a stale save answers 409 rather than
 * overwriting somebody else's choice.
 */
export function useSetReadingMode() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: SetReceiptReadingModeBody) =>
      apiRequest<Envelope<ReceiptReadingModeSaved>>("/review/reading-mode", { method: "PUT", body }),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.reviewLists });
    },
    retry: false,
  });
}

/** How often a screen re-reads while the receipt parser is still reading. */
export const AI_PENDING_POLL_MS = 4000;

export function useReviewItem(itemId: string | undefined) {
  return useQuery({
    queryKey: keys.reviewItem(itemId ?? ""),
    queryFn: () => apiRequest<Envelope<ReviewDetail>>(`/review/${itemId}`),
    enabled: Boolean(itemId),
    refetchInterval: (query) =>
      query.state.data?.data.aiProposal?.status === "pending" ? AI_PENDING_POLL_MS : false,
  });
}

/**
 * "Read with AI": run the receipt parser on one queue item and wait for it.
 * The answer is the proposal, written straight into the item's cache so the
 * form can offer it without a second round trip.
 */
export function useReadWithAi(itemId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<Envelope<AiProposal>>(`/review/${itemId}/parse`, { method: "POST" }),
    onSuccess: (envelope) => {
      client.setQueryData<Envelope<ReviewDetail>>(keys.reviewItem(itemId), (current) =>
        current ? { ...current, data: { ...current.data, aiProposal: envelope.data } } : current,
      );
      void client.invalidateQueries({ queryKey: keys.reviewLists });
    },
    retry: false,
  });
}

export function useWorkOrderTypes() {
  return useQuery({
    queryKey: keys.workOrderTypes,
    queryFn: () => apiRequest<Envelope<ConfigType[]>>("/work-order-types"),
    staleTime: 5 * 60 * 1000,
  });
}

export function useConfig(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.config,
    queryFn: () => apiRequest<Envelope<ConfigType[]>>("/config"),
    // `enabled` so the Settings hub — which every role can open — does not fire
    // an admin-only read for a mechanic and render a 403 beside two rows that
    // loaded fine. The Configuration screen itself leaves it on.
    enabled: options.enabled ?? true,
  });
}

/**
 * The routine-permission switches (F48).
 *
 * Not cached hard, unlike the pickers above. These decide what the database
 * will let people do, and two admins editing them in the same afternoon is the
 * normal case rather than the odd one — a five-minute `staleTime` here would
 * show the second one a policy the first already replaced.
 */
export function useSettingsPermissions(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.settingsPermissions,
    queryFn: () => apiRequest<Envelope<SettingsPermissionsResponse>>("/settings/permissions"),
    enabled: options.enabled ?? true,
  });
}

export function useRootCauses() {
  return useQuery({
    queryKey: keys.rootCauses,
    queryFn: () =>
      apiRequest<Envelope<Array<{ id: string; code: string; name: string }>>>("/root-causes"),
    staleTime: 30 * 60 * 1000,
  });
}

/**
 * The fault-type vocabulary — acceptance criterion A2's "structured fault type".
 *
 * Same half-hour `staleTime` as `useRootCauses`: this is reference data a person
 * edits occasionally, not a figure that goes stale. Note there is no `code` —
 * `reason_type` is keyed on `(parent_id, name)` and has no code column, which is
 * the one shape difference from `root_cause`.
 */
export function useReasonTypes() {
  return useQuery({
    queryKey: keys.reasonTypes,
    queryFn: () =>
      apiRequest<Envelope<Array<{ id: string; name: string; parentId: string | null }>>>(
        "/reason-types",
      ),
    staleTime: 30 * 60 * 1000,
  });
}

/* ── writes ──────────────────────────────────────────────────────── */

/**
 * Everything a mutation touches, invalidated together.
 *
 * A stage change alters the queue, the work order, the board (which shows the
 * stage), the overview counts and — because a completed stage can close a
 * downtime interval — the asset. Invalidating fewer of these leaves a number on
 * screen that the ledger already disagrees with.
 */
function useInvalidate() {
  const client = useQueryClient();
  return {
    workOrder(id: string) {
      void client.invalidateQueries({ queryKey: keys.workOrder(id) });
      void client.invalidateQueries({ queryKey: ["queue"] });
      void client.invalidateQueries({ queryKey: keys.board });
      void client.invalidateQueries({ queryKey: keys.overview });
      void client.invalidateQueries({ queryKey: ["reports"] });
    },
    asset(id: string) {
      void client.invalidateQueries({ queryKey: keys.asset(id) });
      void client.invalidateQueries({ queryKey: ["assets"] });
      void client.invalidateQueries({ queryKey: keys.board });
    },
    vendor(id: string) {
      void client.invalidateQueries({ queryKey: keys.vendor(id) });
      void client.invalidateQueries({ queryKey: ["vendors"] });
    },
    everything() {
      void client.invalidateQueries();
    },
  };
}

export function useTransition(
  workOrderId: string,
): UseMutationResult<TransitionResult, Error, TransitionBody> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: TransitionBody) =>
      apiRequest<TransitionResult>(`/work-orders/${workOrderId}/transition`, {
        method: "POST",
        body,
      }),
    onSettled: () => invalidate.workOrder(workOrderId),
    // No automatic retry. A 409 means the state moved and the user must see the
    // new state; a silent retry would apply their intent to a world that changed.
    retry: false,
  });
}

export function useCreateWorkOrder() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: Omit<CreateWorkOrderBody, "priority"> & { priority?: CreateWorkOrderBody["priority"] }) =>
      apiRequest<{ workOrderId: string }>("/work-orders", { method: "POST", body }),
    onSuccess: () => invalidate.everything(),
    retry: false,
  });
}

export function useEditDueDate(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: EditDueDateBody) =>
      apiRequest<{ dueDate: string | null }>(`/work-orders/${workOrderId}/due-date`, {
        method: "PATCH",
        body,
      }),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

/*
 * ── The two RFP requirement writers — TRBUD-187 remedy (a) ───────────
 *
 * `builtin:vendor` and `builtin:meter_reading` are stage requirements that
 * nothing could satisfy: `tb_app` held the column grants and no route used them,
 * so a stage requiring either refused every forward move with nothing to press.
 * Eden ruled on 8 September 2026 that both get real writers.
 *
 * Both invalidate the work order, which is what makes the Requirements panel
 * re-evaluate: the panel reads the same gate evaluator the transition route
 * runs, so the tick appears because the fact changed rather than because the
 * screen decided it had.
 */
export function useSetWorkOrderVendor(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: SetWorkOrderVendorBody) =>
      apiRequest<{ vendorId: string | null }>(`/work-orders/${workOrderId}/vendor`, {
        method: "PATCH",
        body,
      }),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

/**
 * The other vendors on a work order — TRBUD-304. Both invalidate the whole work
 * order, like the primary-vendor hook above: the vendor list rides on the detail
 * payload, and Add cost's vendor choices are read from it.
 */
export function useAddWorkOrderVendor(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: AddWorkOrderVendorBody) =>
      apiRequest<AddWorkOrderVendorReceipt>(`/work-orders/${workOrderId}/vendors`, {
        method: "POST",
        body,
      }),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

export function useRemoveWorkOrderVendor(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: { workOrderVendorId: string; body: RemoveWorkOrderVendorBody }) =>
      apiRequest<RemoveWorkOrderVendorReceipt>(
        `/work-orders/${workOrderId}/vendors/${input.workOrderVendorId}/remove`,
        { method: "POST", body: input.body },
      ),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

export function useSetWorkOrderMeterReading(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: SetWorkOrderMeterReadingBody) =>
      apiRequest<{ meterReadingId: string | null }>(
        `/work-orders/${workOrderId}/meter-reading`,
        { method: "PATCH", body },
      ),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

/**
 * Set a vendor's status.
 *
 * Invalidates the list as well as the profile, because the badge and the
 * suggestion engine both read it — `buildVendorSuggestion` excludes BLACKLISTED
 * candidates, so a blacklisting that did not refresh the list would keep
 * offering the vendor it just refused.
 */
export function useSetVendorStatus(vendorId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: SetVendorStatusBody) =>
      apiRequest<{ vendorId: string; status: string; displayStatus: string }>(
        `/vendors/${vendorId}/status`,
        { method: "PATCH", body },
      ),
    onSettled: () => invalidate.vendor(vendorId),
    retry: false,
  });
}

/**
 * What a vendor is owed and what has been paid — TRBUD-184.
 *
 * The cache key is `["vendor", id, "payments"]`, a PREFIX EXTENSION of
 * `keys.vendor(id)`. TanStack Query invalidates by prefix and
 * `invalidate.vendor` is already called by every vendor write, so recording a
 * payment refreshes this without any mutation having to learn the read exists —
 * the same arrangement TRBUD-156's requirements panel uses.
 */
export function useVendorPayments(vendorId: string | undefined) {
  return useQuery({
    queryKey: [...keys.vendor(vendorId ?? ""), "payments"],
    queryFn: () =>
      apiRequest<Envelope<VendorPaymentsView>>(`/vendors/${vendorId}/payments`),
    enabled: Boolean(vendorId),
  });
}

export function useRecordVendorPayment(vendorId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: RecordVendorPaymentBody) =>
      apiRequest<Envelope<VendorPaymentReceipt>>(`/vendors/${vendorId}/payments`, {
        method: "POST",
        body,
      }),
    onSettled: () => invalidate.vendor(vendorId),
    retry: false,
  });
}

/**
 * Set or clear a vendor's address — TRBUD-82.
 *
 * Invalidates the list as well as the profile, for the same reason the status
 * mutation does: the address now drives the location filter and the sort, so a
 * saved address that did not refresh the list would leave the vendor filtered
 * out of the very view the user just placed it on.
 */
/**
 * The rest of a vendor's record — TRBUD-263, and the missing half of TRBUD-82.
 *
 * `useSetVendorAddress` below covers the address. `PATCH /vendors/:id/profile`
 * covers everything else a vendor row carries that a human maintains — phone,
 * email, website, notes and the category flags — and until this hook existed it
 * had NO caller anywhere in `apps/web`. A route nothing calls is the same defect
 * as a column nothing writes, arriving from the other end: it was built, granted
 * and tested, and no user could reach it.
 *
 * Invalidates the LIST as well as the profile, for the reason the address
 * mutation gives: the category flags decide which vendors are offered where, so
 * a saved change that did not refresh the list would leave the vendor missing
 * from the view the user just re-categorised it into.
 */
export function useSetVendorProfile(vendorId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: VendorProfileBody) =>
      apiRequest<{ vendorId: string }>(`/vendors/${vendorId}/profile`, {
        method: "PATCH",
        body,
      }),
    onSettled: () => invalidate.vendor(vendorId),
    retry: false,
  });
}

/**
 * Retire one of a vendor's contacts (record-details plan, section 8). The
 * contact stays on the profile, marked retired, and leaves the assign picker.
 */
export function useRetireVendorContact(vendorId: string) {
  const invalidate = useInvalidate();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (contactId: string) =>
      apiRequest<{ vendorId: string; contactId: string; active: boolean }>(
        `/vendors/${vendorId}/contacts/${contactId}/retire`,
        { method: "POST", body: { idempotencyKey: newIdempotencyKey("vendor-contact-retire") } },
      ),
    onSettled: () => {
      invalidate.vendor(vendorId);
      // The assign picker lists active contacts, so a retired one must leave it.
      void client.invalidateQueries({ queryKey: keys.assignableParties });
    },
    retry: false,
  });
}

/**
 * Add a contact to a vendor, or edit one — TRBUD-466.
 *
 * Both refresh the profile and the list (the completeness counts sit under the
 * list's key), and the work-order assign picker, which lists active contacts: a
 * new contact must appear there and a renamed one must read its new name.
 */
export function useAddVendorContact(vendorId: string) {
  const invalidate = useInvalidate();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateVendorContactBody) =>
      apiRequest<{ data: { vendorId: string; contactId: string } }>(`/vendors/${vendorId}/contacts`, {
        method: "POST",
        body,
      }),
    onSettled: () => {
      invalidate.vendor(vendorId);
      void client.invalidateQueries({ queryKey: keys.assignableParties });
    },
    retry: false,
  });
}

export function useEditVendorContact(vendorId: string, contactId: string) {
  const invalidate = useInvalidate();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: EditVendorContactBody) =>
      apiRequest<{ data: { vendorId: string; contactId: string } }>(
        `/vendors/${vendorId}/contacts/${contactId}`,
        { method: "PATCH", body },
      ),
    onSettled: () => {
      invalidate.vendor(vendorId);
      void client.invalidateQueries({ queryKey: keys.assignableParties });
    },
    retry: false,
  });
}

export function useSetVendorAddress(vendorId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: VendorAddressBody) =>
      apiRequest<{ vendorId: string }>(`/vendors/${vendorId}/address`, {
        method: "PATCH",
        body,
      }),
    onSettled: () => invalidate.vendor(vendorId),
    retry: false,
  });
}

/**
 * Vendors near a work order, for outsourcing the labour.
 *
 * `enabled` is left on unconditionally: the endpoint answers usefully even when
 * it cannot rank — `basis: "none"` with the eligible/placed counts is what the
 * panel needs in order to say why, and skipping the request would leave the
 * screen unable to distinguish "nothing nearby" from "not asked".
 */
export const nearbyVendorsQuery = (workOrderId: string) =>
  queryOptions({
    queryKey: ["nearby-vendors", workOrderId] as const,
    queryFn: () =>
      apiRequest<Envelope<NearbyVendors>>(`/work-orders/${workOrderId}/nearby-vendors`),
  });

export function useNearbyVendors(workOrderId: string | undefined) {
  return useQuery({
    ...nearbyVendorsQuery(workOrderId ?? ""),
    enabled: Boolean(workOrderId),
  });
}

export function usePostComment(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: PostCommentBody) =>
      apiRequest<{ commentId: string }>(`/work-orders/${workOrderId}/comments`, {
        method: "POST",
        body,
      }),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

/**
 * The comment eraser (migration 0268). The work-order sweep is the whole of it:
 * the words leave the timeline and the queue's last-update column, both of which
 * that sweep refetches, and search is rebuilt in the same transaction.
 */
export function useEraseComment(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: { commentId: string; body: EraseCommentBody }) =>
      apiRequest<{ commentId: string; erased: true }>(
        `/work-orders/${workOrderId}/comments/${input.commentId}/erase`,
        { method: "POST", body: input.body },
      ),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

export function useLogTime(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: LogTimeBody) =>
      /*
       * `timeEntryId` is the time entry's uuid and is what the void and correct
       * routes take; `costEntryId` is the labour valuation's bigint id. Until
       * UAT 0908 finding 3 the route returned the SECOND under the FIRST's
       * name, so this declaration was accurate about the field and wrong about
       * the value — nothing rendered it, which is the only reason it never
       * surfaced. `timeEntryId` is nullable because it is read back off
       * `cost_entry`, and a type that promised a string would be a promise this
       * layer cannot keep.
       */
      apiRequest<{ timeEntryId: string | null; costEntryId: string }>(
        `/work-orders/${workOrderId}/time`,
        { method: "POST", body },
      ),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

/**
 * Correct a posted time entry — TRBUD-178.
 *
 * `invalidate.workOrder` is what `useLogTime` and `useReverseCost` use, and it is
 * the right sweep here for the same reason it is there: the correction moves the
 * time panel, the cost panel and the posted total on one screen, plus the queue,
 * board and overview a correction feeds. `reports` is invalidated on top, which
 * neither neighbour does — a correction rewrites `v_labour_roi` and
 * `v_departmental_spend`, and those are exactly the numbers this exists to make
 * honest, so leaving a stale Reports tab open would show the figure the user has
 * just corrected.
 */
export function useCorrectTime(workOrderId: string) {
  const client = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: { timeEntryId: string; body: CorrectTimeBody }) =>
      apiRequest<{
        timeEntryId: string;
        supersededTimeEntryId: string;
        minutes: number;
        totalMinutes: number;
        totalLabourValue: string | null;
      }>(`/work-orders/${workOrderId}/time/${input.timeEntryId}/correct`, {
        method: "POST",
        body: input.body,
      }),
    onSettled: () => {
      invalidate.workOrder(workOrderId);
      void client.invalidateQueries({ queryKey: ["reports"] });
    },
    retry: false,
  });
}

export function useAddCost(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: AddCostBody) =>
      apiRequest<{ costEntryId: string }>(`/work-orders/${workOrderId}/costs`, {
        method: "POST",
        body,
      }),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

export function useReverseCost(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: { costEntryId: string; body: ReverseCostBody }) =>
      apiRequest<{ reversalCostEntryId: string }>(
        `/work-orders/${workOrderId}/costs/${input.costEntryId}/reverse`,
        { method: "POST", body: input.body },
      ),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

/**
 * Void a posted time entry — TRBUD-183.
 *
 * `reports` is invalidated on top of the work-order sweep, exactly as
 * `useCorrectTime` does it and for the same reason: a void rewrites
 * `v_labour_roi` and `v_departmental_spend`, which are the numbers the feature
 * exists to make honest. `useReverseCost` does NOT invalidate reports, and that
 * is a pre-existing gap rather than a precedent to copy — see the plan doc.
 */
export function useVoidTime(workOrderId: string) {
  const client = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: { timeEntryId: string; body: VoidTimeBody }) =>
      apiRequest<{
        voidId: string;
        voidedTimeEntryId: string;
        totalMinutes: number;
        totalLabourValue: string | null;
      }>(`/work-orders/${workOrderId}/time/${input.timeEntryId}/void`, {
        method: "POST",
        body: input.body,
      }),
    onSettled: () => {
      invalidate.workOrder(workOrderId);
      void client.invalidateQueries({ queryKey: ["reports"] });
    },
    retry: false,
  });
}

/** Void a posted cost — TRBUD-183. Same invalidation reasoning as `useVoidTime`. */
export function useVoidCost(workOrderId: string) {
  const client = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: { costEntryId: string; body: VoidCostBody }) =>
      apiRequest<{ voidId: string; voidedCostEntryId: number; postedTotal: string }>(
        `/work-orders/${workOrderId}/costs/${input.costEntryId}/void`,
        { method: "POST", body: input.body },
      ),
    onSettled: () => {
      invalidate.workOrder(workOrderId);
      void client.invalidateQueries({ queryKey: ["reports"] });
    },
    retry: false,
  });
}

export function useSetRootCause(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: SetRootCauseBody) =>
      apiRequest<{ rootCauseId: string | null }>(`/work-orders/${workOrderId}/root-cause`, {
        method: "PATCH",
        body,
      }),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

/**
 * Record the fault type on an existing work order.
 *
 * `invalidate.workOrder` is the whole refresh: the set rides on
 * `WorkOrderDetail` (`reasonTypeIds`), so invalidating the work order refetches
 * the picker's own value. The shim read this used to keep in step by key prefix
 * is gone, and with it the extra request every mount of the screen paid for.
 *
 * PUT and set-the-list since TRBUD-266, matching the categories writer: a
 * replace is idempotent, so a retried submit after a signal drop cannot
 * double-apply it.
 */
export function useSetWorkOrderFaults(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: SetWorkOrderFaultsBody) =>
      apiRequest<{ reasonTypeIds: string[] }>(`/work-orders/${workOrderId}/faults`, {
        method: "PUT",
        body,
      }),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

/**
 * Add a fault type from the picker that needed it (requirement 11.23).
 *
 * `onSuccess` **returns** the invalidation promise rather than firing and
 * forgetting it, and that is the whole point rather than housekeeping: the
 * caller selects the new id straight away, and a `<select>` cannot hold a value
 * that has no `<option>` yet. query-core awaits this hook-level `onSuccess`
 * before it dispatches success, and the per-call `onSuccess` passed to `mutate`
 * runs on that dispatch — so the list is already refetched by the time the
 * caller selects, and "added" and "selectable" are the same instant.
 *
 * `staleTime` on `useReasonTypes` is half an hour, which is right for reference
 * data and would otherwise mean a type you just created did not appear until the
 * tab was reloaded. Invalidation overrides it; nothing else would.
 */
export function useCreateReasonType() {
  const invalidateFaultTypes = useInvalidateFaultTypes();
  return useMutation({
    mutationFn: (body: CreateReasonTypeBody) =>
      apiRequest<Envelope<{ id: string; name: string; parentId: string | null }>>(
        "/reason-types",
        { method: "POST", body },
      ),
    onSuccess: invalidateFaultTypes,
    retry: false,
  });
}

/**
 * Both fault-type writers invalidate BOTH lists — the picker's and /config's.
 *
 * Same rule as `useInvalidateCategories`, and it is not symmetric in what it
 * fixes: a RENAME has to reach the picker or the two screens disagree about one
 * row, and a DEACTIVATION has to leave the picker while STAYING in /config,
 * which is the whole point of the admin list including inactive rows.
 *
 * ── The returned promise is load-bearing for the create path ────────
 * `useCreateReasonType` awaits this, and that is deliberate rather than tidy:
 * `FaultTypePicker` selects the new id the instant the mutation resolves, and a
 * check row cannot hold a value whose option does not exist yet. query-core
 * awaits the hook-level `onSuccess` before dispatching success, so "added" and
 * "selectable" are the same instant. Returning `Promise.all` keeps that
 * guarantee while adding the admin list, which costs nothing when that list has
 * no observer — an invalidation with nothing watching marks stale and refetches
 * nothing.
 *
 * `useReasonTypes` carries a half-hour `staleTime`, right for reference data,
 * which would otherwise mean a type you just renamed kept its old name until the
 * tab was reloaded. Invalidation overrides it; nothing else would.
 */
function useInvalidateFaultTypes() {
  const client = useQueryClient();
  return () =>
    Promise.all([
      client.invalidateQueries({ queryKey: keys.reasonTypes }),
      client.invalidateQueries({ queryKey: keys.configFaultTypes }),
    ]);
}

/**
 * Every fault type including the retired ones, with what carries each.
 *
 * `enabled` for the reason `useConfigCategories` has it: the endpoint requires
 * the `configuration` capability, so firing it for a mechanic would render a 403
 * beside panels that loaded fine.
 */
export function useConfigFaultTypes(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.configFaultTypes,
    queryFn: () => apiRequest<Envelope<FaultTypeAdminRow[]>>("/config/fault-types"),
    enabled: options.enabled ?? true,
  });
}

/**
 * Rename, re-describe or retire a fault type.
 *
 * PATCH and partial, matching `useUpdateCategory`: the body carries only what
 * changed, so an omitted field keeps its value server-side rather than being
 * echoed back at the same value.
 *
 * Retiring is the removal for a fault type in use, which is what `active` on
 * this body is: the link FK is ON DELETE RESTRICT and tb_app holds no DELETE on
 * `reason_type`, because a fault type that labelled closed work is history. An
 * UNUSED one can be deleted (TRBUD-448) through `useDeleteFaultType` in
 * `screens/config/config-delete-queries.ts`, which calls `delete_fault_type`.
 */
export function useUpdateReasonType(reasonTypeId: string) {
  const invalidateFaultTypes = useInvalidateFaultTypes();
  return useMutation({
    mutationFn: (body: UpdateReasonTypeBody) =>
      apiRequest<FaultTypeReceipt>(`/config/fault-types/${reasonTypeId}`, {
        method: "PATCH",
        body,
      }),
    onSettled: invalidateFaultTypes,
    retry: false,
  });
}

/**
 * The cost-status ruling. `postCosts` only — the button is not rendered for a
 * role that lacks it, and the API refuses it besides.
 */
export function useSetCostStatus(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: SetCostStatusBody) =>
      apiRequest<{ costStatus: string }>(`/work-orders/${workOrderId}/cost-status`, {
        method: "PATCH",
        body,
      }),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

/**
 * The assign picker. Staff-only, so it is not fetched for a role that cannot assign.
 *
 * Pass `workOrderId` so the server can drop roles that could not act on this
 * particular job — a mechanic is not offered for a Roadside work order, because
 * no Roadside transition admits them and they would be stuck the moment they
 * took it. Omitting it returns the unfiltered roster.
 */
export function useAssignableParties(
  options: { enabled?: boolean; workOrderId?: string } = {},
) {
  const { workOrderId } = options;
  return useQuery({
    queryKey: [...keys.assignableParties, workOrderId ?? null] as const,
    queryFn: () =>
      apiRequest<Envelope<AssignableParty[]>>(
        workOrderId
          ? `/assignable-parties?workOrderId=${encodeURIComponent(workOrderId)}`
          : "/assignable-parties",
      ),
    // The roster changes rarely; a picker does not need to refetch on focus.
    staleTime: 5 * 60 * 1000,
    enabled: options.enabled ?? true,
  });
}

export function useAssignWorkOrder(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: AssignWorkOrderBody) =>
      apiRequest<{ assignmentId: string }>(`/work-orders/${workOrderId}/assignments`, {
        method: "POST",
        body,
      }),
    // `invalidate.workOrder` already sweeps the queue, board, overview and
    // reports — which matters here, because assignment decides what a mechanic's
    // own-work filter returns and a stale queue would hide work just claimed.
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

export function useUnassignWorkOrder(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: { assignmentId: string; body: UnassignWorkOrderBody }) =>
      apiRequest<{ assignmentId: string }>(
        `/work-orders/${workOrderId}/assignments/${input.assignmentId}/unassign`,
        { method: "POST", body: input.body },
      ),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

export function useCompleteProcedureStep(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: { stepId: string; body: CompleteProcedureStepBody }) =>
      apiRequest<{ completed: boolean }>(
        `/work-orders/${workOrderId}/procedure-steps/${input.stepId}`,
        { method: "POST", body: input.body },
      ),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

/**
 * Create an asset by hand — the one path that does not come from a vendor
 * projection.
 *
 * `invalidate.asset(result.assetId)` rather than `everything()`, which is what
 * the other create hooks reach for. A new work order changes costs, the board,
 * the overview and the reports, so sweeping the whole cache is proportionate
 * there. A new asset changes exactly one query family: the Assets list, whose
 * rows, class counts, filter options and heat-map buckets all arrive in the same
 * payload, plus the status board that groups by location. Nothing about a work
 * order, a cost or a part moved, so refetching those would be a screen-wide
 * reload for a row that is not on them yet.
 *
 * Invalidating `keys.asset(newId)` in passing is a no-op — nothing is cached
 * under an id that did not exist a moment ago — and costs nothing to keep, so
 * the declared asset invalidation set stays in one place rather than being
 * copied here minus a line.
 */
export function useCreateAsset() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: CreateAssetBody) =>
      apiRequest<{ assetId: string }>("/assets", { method: "POST", body }),
    onSuccess: (result) => invalidate.asset(result.assetId),
    retry: false,
  });
}

export function useMarkAssetDown(assetId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: MarkAssetDownBody) =>
      apiRequest<{ downtimeId: string }>(`/assets/${assetId}/mark-down`, { method: "POST", body }),
    onSettled: () => invalidate.asset(assetId),
    retry: false,
  });
}

/**
 * Retire, decommission, or return an asset to active — TRBUD-92 / TICKET-818.
 *
 * `invalidate.asset` rather than writing the receipt into the cache: the routine
 * is a state MACHINE that returns early when the status already matches and
 * refuses several transitions outright, so the authoritative answer to "what is
 * this asset now" is the server's re-read, not the status we asked for.
 */
export function useSetAssetLifecycle(assetId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: SetAssetLifecycleBody) =>
      apiRequest<AssetLifecycleReceipt>(`/assets/${assetId}/lifecycle`, {
        method: "POST",
        body,
      }),
    onSettled: () => invalidate.asset(assetId),
    retry: false,
  });
}

export function useBackInService(assetId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: BackInServiceBody) =>
      apiRequest<BackInServiceResponse>(`/assets/${assetId}/back-in-service`, { method: "POST", body }),
    onSettled: () => invalidate.asset(assetId),
    retry: false,
  });
}

export function useIssuePart() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: IssuePartBody) =>
      apiRequest<{ inventoryTxnId: string; costEntryId: string }>("/parts/issue", {
        method: "POST",
        body,
      }),
    onSuccess: () => invalidate.everything(),
    retry: false,
  });
}

/**
 * Stocking locations, for the Receive stock picker.
 *
 * Static configuration that changes about never, so it is cached hard rather than
 * refetched per dialog open.
 */
/**
 * Create a stocking location — the cold-start hole from F-06 / TRBUD-113.
 *
 * `POST /locations` has existed since that fix and had no caller anywhere in
 * `apps/web`, so a tester could see an empty location picker and had no way to
 * fill it. Without a location there is no `deliverToLocationId`, and without that
 * no purchase order can be raised at all.
 *
 * Returns `LocationOption` rather than a receipt shape of its own, which is
 * exactly what the pickers already speak — so invalidating `keys.locations` is
 * enough and no row has to be spliced by hand.
 */
export function useCreateLocation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLocationBody) =>
      apiRequest<Envelope<LocationOption>>("/locations", { method: "POST", body }),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: keys.locations }),
        client.invalidateQueries({ queryKey: ["time-clock"] }),
      ]);
    },
    retry: false,
  });
}

/**
 * Add a part to the catalogue.
 *
 * The response carries a `warning` when no unit cost was given, and the caller
 * MUST surface it: `POST /parts/issue` declines an unvalued part outright, and
 * `part.unit_cost` cannot be updated afterwards — `02-grants.sql` revokes it from
 * `tb_app` and no setter routine exists yet. So a part created without a price is
 * permanently unissuable, and the moment to say so is now.
 */
export function useCreatePart() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePartBody) =>
      apiRequest<Envelope<{ partId: string; partNumber: string; warning: string | null }>>(
        "/parts",
        { method: "POST", body },
      ),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["parts"] }),
    retry: false,
  });
}

export function useLocations(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.locations,
    queryFn: () => apiRequest<Envelope<LocationOption[]>>("/locations"),
    staleTime: 5 * 60 * 1000,
    enabled: options.enabled ?? true,
  });
}

/**
 * Receive a part onto a shelf.
 *
 * `invalidate.everything()` rather than just the parts list: receiving changes
 * on-hand, which the Parts table, its extended value total and the issue dialog's
 * own eligibility all read.
 */
export function useReceivePart() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: ReceivePartBody) =>
      apiRequest<{
        inventoryTxnId: string;
        partNumber: string;
        locationName: string;
        quantity: string;
      }>("/parts/receive", { method: "POST", body }),
    onSuccess: () => invalidate.everything(),
    retry: false,
  });
}

/**
 * The issues of one part that could still be returned — TRBUD-124 / F-33.
 *
 * `enabled` is passed rather than assumed, because this only ever runs while the
 * return dialog is open. `staleTime: 0`: the list is "issues nothing has
 * returned yet", and offering an issue somebody else has already returned would
 * put a guaranteed 23505 in front of the user.
 */
export function useReturnableIssues(partId: string | undefined) {
  return useQuery({
    queryKey: keys.returnableIssues(partId ?? ""),
    queryFn: () =>
      apiRequest<Envelope<ReturnableIssueRow[]>>(`/parts/${partId}/returnable-issues`),
    enabled: partId !== undefined,
    staleTime: 0,
  });
}

/**
 * Return an issued part to the shelf — TRBUD-124 / F-33, ruling D10.
 *
 * `invalidate.everything()` like issue and receive, because a return moves the
 * on-hand position, the returnable-issue list and the work order's ledger view
 * at once.
 *
 * **It does NOT reverse the cost, and the dialog says so.** D10: the money
 * coming back and the part coming back are two separate human assertions.
 */
export function useReturnPart() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: ReturnPartBody) =>
      apiRequest<{
        inventoryTxnId: string;
        returnedFromTxnId: string;
        partNumber: string;
        quantity: string;
      }>("/parts/return", { method: "POST", body }),
    onSuccess: () => invalidate.everything(),
    retry: false,
  });
}

/**
 * One part's stock ledger, and which rows can still be reversed — TRBUD-180.
 *
 * `staleTime: 0` like `useReturnableIssues` beside it, and for the same reason:
 * the answer changes the moment anybody moves stock, and a cached list would
 * offer a control on a row the routine has since refused.
 */
export function usePartMovements(partId: string | undefined) {
  return useQuery({
    queryKey: ["parts", partId ?? "", "movements"] as const,
    queryFn: () => apiRequest<Envelope<PartMovementRow[]>>(`/parts/${partId}/movements`),
    enabled: partId !== undefined,
    staleTime: 0,
  });
}

/**
 * Reverse a stock movement recorded in error — TRBUD-180.
 *
 * `invalidate.everything()` like the other stock writes, because a reversal
 * moves the on-hand position, this movement list, the returnable-issue list and
 * the work order's ledger view at once.
 *
 * DISTINCT from `useReturnPart` above: that one says a quantity of an issue came
 * back; this one says the movement should not have been recorded at all. Note it
 * does not touch the COST either — the same D10 reasoning applies, and a
 * reversal that silently un-charged a work order would be the system asserting an
 * accounting fact from a physical one.
 */
export function useReverseMovement() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: ReverseMovementBody) =>
      apiRequest<ReverseMovementReceipt>("/parts/reverse-movement", { method: "POST", body }),
    onSuccess: () => invalidate.everything(),
    retry: false,
  });
}

/**
 * The shelves that actually hold one part — the transfer dialog's origin picker,
 * TRBUD-250 line B.
 *
 * NOT `useLocations`, and the asymmetry is the design. Receiving needs every
 * location, because the point of receiving is to put a part somewhere it has
 * never been. A transfer's ORIGIN must be a shelf that holds it, and a picker
 * offering all seven would make the operator guess — after which the routine
 * refuses the guess. The DESTINATION picker still uses `useLocations`.
 *
 * `staleTime: 0` like `useReturnableIssues` and `usePartMovements` beside it: the
 * answer changes the moment anybody moves stock, and a cached quantity would let
 * the dialog offer a transfer the routine has since refused.
 */
export function usePartStockLocations(partId: string | undefined) {
  return useQuery({
    queryKey: ["parts", partId ?? "", "stock-locations"] as const,
    queryFn: () =>
      apiRequest<Envelope<PartStockLocation[]>>(`/parts/${partId}/stock-locations`),
    enabled: partId !== undefined,
    staleTime: 0,
  });
}

/**
 * Move stock between two shelves — TRBUD-250 line B.
 *
 * `invalidate.everything()` like every other stock write, because a transfer
 * moves the on-hand position at two locations at once, and the Parts table, its
 * extended-value total, this origin list and the movements list all read it.
 */
export function useTransferStock() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: TransferStockBody) =>
      apiRequest<TransferStockReceipt>("/parts/transfer", { method: "POST", body }),
    onSuccess: () => invalidate.everything(),
    retry: false,
  });
}

/**
 * Record a count of one shelf — TRBUD-250 line C.
 *
 * `invalidate.everything()`, like every other stock mutation here, and it earns
 * it: a count that differs writes an `adjust` row, so the part row's on-hand, the
 * movement history and every valuation over it all move at once.
 *
 * A count that MATCHES writes no ledger row and still invalidates, deliberately.
 * Nothing on the Parts screen reads the count itself yet, so this refetch is
 * strictly redundant in that case — but making it conditional on
 * `receipt.variance` would put a caching rule in the one place a reader would
 * never look for one, to save a request nobody is waiting on.
 */
export function useRecordStockCount(partId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: RecordStockCountBody) =>
      apiRequest<StockCountReceipt>(`/parts/${partId}/count`, { method: "POST", body }),
    onSuccess: () => invalidate.everything(),
    retry: false,
  });
}

export function useSubmitDriverRequest() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: DriverRequestBody) =>
      apiRequest<{ requestId: string }>("/requests", { method: "POST", body }),
    onSuccess: () => invalidate.everything(),
    retry: false,
  });
}

export function useLogRequest() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: LogRequestBody) =>
      apiRequest<{ requestId: string }>("/requests/log", { method: "POST", body }),
    onSuccess: () => invalidate.everything(),
    retry: false,
  });
}

export function useConvertRequest() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: { requestId: string; body: ConvertRequestBody }) =>
      apiRequest<{ workOrderId: string }>(`/requests/${input.requestId}/convert`, {
        method: "POST",
        body: input.body,
      }),
    onSuccess: () => invalidate.everything(),
    retry: false,
  });
}

export function useDeclineRequest() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: { requestId: string; body: DeclineRequestBody }) =>
      apiRequest<{ ok: boolean }>(`/requests/${input.requestId}/decline`, {
        method: "POST",
        body: input.body,
      }),
    onSuccess: () => invalidate.everything(),
    retry: false,
  });
}

export function useRequestInbox(params: Partial<RequestsInboxQuery>) {
  return useQuery({
    queryKey: keys.requestInbox(params),
    queryFn: () => apiRequest<Envelope<RequestInboxResponse>>("/requests/inbox", { query: params }),
  });
}

/** How many inbox rows one page of the requests screen asks for. */
export const REQUEST_INBOX_PAGE = 50;

/**
 * The inbox a page at a time, for the requests screen's "Load more". The
 * filters are the query key, so changing one starts again from the first page;
 * the previous pages stay on screen while the new first page loads.
 */
export function useRequestInboxPages(params: Omit<Partial<RequestsInboxQuery>, "limit" | "offset">) {
  return useInfiniteQuery({
    queryKey: [...keys.requestInbox(params), "pages"] as const,
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      apiRequest<Envelope<RequestInboxResponse>>("/requests/inbox", {
        query: { ...params, limit: REQUEST_INBOX_PAGE, offset: pageParam },
      }),
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.data.rows.length, 0);
      return last.data.rows.length > 0 && loaded < last.data.total ? loaded : undefined;
    },
    placeholderData: keepPreviousData,
  });
}

export function useRequestDetail(id: string | undefined) {
  return useQuery({
    queryKey: keys.requestDetail(id ?? ""),
    queryFn: () => apiRequest<Envelope<RequestDetail>>(`/requests/${id}`),
    enabled: id !== undefined,
  });
}

/**
 * One line's accept / reject / correct decision.
 *
 * **There is deliberately no `setQueryData` here.** The route answers with a
 * `ReviewLineDecisionReceipt` — one line's outcome — not the whole panel. Writing
 * that receipt into `keys.reviewItem` replaced the cached `ReviewDetail` with a
 * seven-field object, so the next render hit `detail.lines.map` on `undefined` and
 * blanked the screen. The server stays the single source of the panel's state; the
 * `onSettled` invalidation refetches it.
 */
export function useReviewLineDecision(itemId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: ReviewLineDecisionBody) =>
      apiRequest<ReviewLineDecisionReceipt>(`/review/${itemId}/lines`, { method: "POST", body }),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.reviewItem(itemId) });
      // A decided line can move the item between tabs (TRBUD-443).
      invalidateNavCounts(client);
    },
    retry: false,
  });
}

/**
 * Confirm a logged invoice. The body names what it was logged against: the work
 * order for an ordinary invoice, which posts its lines as job cost, or the
 * purchase order for a bill, which posts none (docs/plans/trbud-po-bills.md).
 */
export function useConfirmInvoice(itemId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: ConfirmInvoiceBody) =>
      apiRequest<ConfirmInvoiceReceipt>(`/review/${itemId}/confirm`, {
        method: "POST",
        body,
      }),
    onSuccess: () => invalidate.everything(),
    retry: false,
  });
}

export function useMarkPoDocumentReviewed(itemId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () => apiRequest<{ reviewed: boolean }>(`/review/${itemId}/mark-po-reviewed`, { method: "POST" }),
    onSuccess: () => invalidate.everything(),
    retry: false,
  });
}

/**
 * Log a vendor's invoice by hand, itemised, against a work order — TRBUD-162.
 *
 * `invalidate.everything()` on success rather than a targeted key, for the same
 * reason `useConfirmInvoice` does it: this one request creates a `vendor_invoice`
 * AND an `extraction` AND its lines, so it changes the review queue, the work
 * order's documents panel and — once confirmed — the ledger and the stage gates.
 * A narrow invalidation here is how a screen comes to disagree with the database
 * it just wrote to.
 */
export function useLogManualInvoice(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: CreateManualInvoiceBody) =>
      apiRequest<Envelope<ManualInvoiceReceipt>>(`/work-orders/${workOrderId}/manual-invoice`, {
        method: "POST",
        body,
      }),
    onSuccess: () => invalidate.everything(),
    retry: false,
  });
}

/**
 * Log a vendor's bill against a PURCHASE ORDER: the receipts the MaintainX import
 * attached to its orders (docs/plans/trbud-po-bills.md).
 *
 * The same answer and the same `invalidate.everything()` as `useLogManualInvoice`,
 * for the same reason: one request writes a `vendor_invoice`, the review item's
 * header and its lines, and the item moves from Unaudited to Logged.
 */
export function useLogPurchaseOrderBill(purchaseOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: CreatePurchaseOrderBillBody) =>
      apiRequest<Envelope<ManualInvoiceReceipt>>(`/purchase-orders/${purchaseOrderId}/bill`, {
        method: "POST",
        body,
      }),
    onSuccess: () => invalidate.everything(),
    retry: false,
  });
}

/**
 * Create a work-order type and its starting pipeline.
 *
 * Invalidates `workOrderTypes` as well as `config`, because a new type is
 * immediately selectable in the New Work Order dialog — leaving that list stale
 * would hide a type the user just made.
 */
export function useCreateWorkOrderType() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateWorkOrderTypeBody) =>
      apiRequest<CreatedWorkOrderType>("/config/types", { method: "POST", body }),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.config });
      void client.invalidateQueries({ queryKey: keys.workOrderTypes });
    },
    retry: false,
  });
}

export function usePublishConfig(typeId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { idempotencyKey: string }) =>
      apiRequest<{ schemaId: string; version: number }>(`/config/${typeId}/publish`, {
        method: "POST",
        body,
      }),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.config });
      void client.invalidateQueries({ queryKey: keys.workOrderTypes });
    },
    retry: false,
  });
}

export function useEditStage(typeId: string, stageId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: EditStageBody) =>
      apiRequest<Envelope<StageEditReceipt>>(`/config/${typeId}/stages/${stageId}`, {
        method: "PATCH",
        body,
      }),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.config });
    },
    retry: false,
  });
}

/**
 * Save the permission draft — the whole diff, in one request.
 *
 * `invalidate.everything()` and not just the switches, and the reason is the
 * point of the feature: these rows are read by `tb_require_actor_role` on every
 * controlled routine, so changing one changes what the *server* will accept from
 * whoever is looking at any other screen. The session's capability mirror is
 * separate and compile-time, so it does not move — but every list whose actions
 * are decided by the database now might.
 */
export function useSaveSettingsPermissions() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: SetRoutinePermissionsBody) =>
      apiRequest<SetRoutinePermissionsReceipt>("/settings/permissions", {
        method: "PATCH",
        body,
      }),
    onSuccess: () => invalidate.everything(),
    // No retry. A 422 is a refusal the user has to read and answer, and
    // re-sending it changes nothing except how many times it is logged.
    retry: false,
  });
}

export function useAddCustomField(typeId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      key: string;
      label: string;
      dataType: "date" | "currency" | "boolean";
      currencyCode?: string | null;
      isRequired: boolean;
      idempotencyKey: string;
    }) =>
      apiRequest<{ customFieldId: string }>(`/config/${typeId}/custom-fields`, {
        method: "POST",
        body,
      }),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.config });
    },
    retry: false,
  });
}

/**
 * Release a draft — one way, and there is no matching un-release.
 *
 * `invalidate.workOrder` rather than a narrower key because releasing changes who
 * can see the row at all: the queue, the board and the overview each gain a work
 * order that was not in them a moment ago.
 */
export function useReleaseDraft(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: ReleaseDraftBody) =>
      apiRequest<{ releasedAt: string }>(`/work-orders/${workOrderId}/release`, {
        method: "POST",
        body,
      }),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

/* ── work-order categories ───────────────────────────────────────── */

/**
 * The categories a form may offer. Active only, and cached for a while — this is
 * user-managed configuration that changes when somebody edits it, not per-request
 * data, and every work-order form reads it.
 *
 * `enabled` (TRBUD-441) lets the queue ask only while a category filter is on,
 * to name it on the count line, rather than on every queue load.
 */
export function useCategoryOptions(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.categoryOptions,
    queryFn: () => apiRequest<Envelope<CategoryOption[]>>("/categories"),
    staleTime: 5 * 60 * 1000,
    enabled: options.enabled ?? true,
  });
}

/**
 * Every category including the deactivated ones, with a count of what carries it.
 *
 * `enabled` for the same reason `useConfig` has it: this endpoint requires the
 * `configuration` capability, and firing it for a mechanic would render a 403
 * beside panels that loaded fine.
 */
export function useConfigCategories(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.configCategories,
    queryFn: () => apiRequest<Envelope<CategoryAdminRow[]>>("/config/categories"),
    enabled: options.enabled ?? true,
  });
}

/**
 * Key and fetcher for one work order's categories, shared by the hook and by the
 * work-order screen's early start (`useStartWorkOrderSideReads`, TRBUD-448), so
 * the two can never ask under different keys.
 */
export const workOrderCategoriesQuery = (workOrderId: string) =>
  queryOptions({
    queryKey: keys.workOrderCategories(workOrderId),
    queryFn: () =>
      apiRequest<Envelope<CategoryOption[]>>(`/work-orders/${workOrderId}/categories`),
  });

export function useWorkOrderCategories(workOrderId: string | undefined) {
  return useQuery({
    ...workOrderCategoriesQuery(workOrderId ?? ""),
    enabled: Boolean(workOrderId),
  });
}

/**
 * Replace a work order's whole category set.
 *
 * PUT, not PATCH, and the body is the intended final set rather than a delta —
 * `tb_app` holds INSERT and DELETE on the link table and no UPDATE, so re-tagging
 * is a delete plus an insert whichever way the request is shaped. Sending the set
 * makes it idempotent by construction.
 */
export function useSetWorkOrderCategories(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: SetWorkOrderCategoriesBody) =>
      apiRequest<SetWorkOrderCategoriesReceipt>(`/work-orders/${workOrderId}/categories`, {
        method: "PUT",
        body,
      }),
    // `invalidate.workOrder` covers the categories key too: it is
    // ["work-order", id, "categories"], so invalidating ["work-order", id]
    // matches it by prefix. The queue and board also change, because the
    // category feeds the work-order search document.
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

/**
 * Both category writers invalidate BOTH category lists.
 *
 * A rename shows up in the picker as well as in /config, and a deactivation has to
 * leave the picker even though it stays in /config. Invalidating only the admin
 * list would leave every open form offering a category nobody can use.
 */
function useInvalidateCategories() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: keys.configCategories });
    void client.invalidateQueries({ queryKey: keys.categoryOptions });
  };
}

export function useCreateCategory() {
  const invalidateCategories = useInvalidateCategories();
  return useMutation({
    mutationFn: (body: CreateWorkOrderCategoryBody) =>
      apiRequest<WorkOrderCategoryReceipt>("/config/categories", { method: "POST", body }),
    onSettled: invalidateCategories,
    retry: false,
  });
}

/**
 * Procedures and their steps, for the /config editor (TRBUD-170).
 *
 * `enabled` for the same reason `useConfigCategories` has it: the endpoint
 * requires the `configuration` capability, and firing it for a mechanic would
 * render a 403 beside panels that loaded fine.
 */
export function useConfigProcedures(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.configProcedures,
    queryFn: () => apiRequest<Envelope<ProcedureAdminRow[]>>("/config/procedures"),
    enabled: options.enabled ?? true,
  });
}

/**
 * Create a procedure with its steps.
 *
 * There is no `useUpdateProcedure`, and that is the D6 ruling rather than an
 * omission: tb_app holds no UPDATE on `procedure`, so editing is expressed as
 * creating a new version through this same mutation. See the route header in
 * `apps/api/src/routes/config-vocabularies.ts`.
 */
export function useCreateProcedure() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateProcedureBody) =>
      apiRequest<ProcedureReceipt>("/config/procedures", { method: "POST", body }),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.configProcedures });
    },
    retry: false,
  });
}

export function useUpdateCategory(categoryId: string) {
  const invalidateCategories = useInvalidateCategories();
  return useMutation({
    mutationFn: (body: UpdateWorkOrderCategoryBody) =>
      apiRequest<WorkOrderCategoryReceipt>(`/config/categories/${categoryId}`, {
        method: "PATCH",
        body,
      }),
    onSettled: invalidateCategories,
    retry: false,
  });
}

/**
 * What the current stage still requires, why, and where to enter it — TRBUD-156.
 *
 * Its own read rather than a field on `useWorkOrder`, so a write that satisfies
 * one requirement refreshes this card without re-running the detail payload's
 * twelve reads. The server decides everything shown: the verdict comes from the
 * same evaluator the transition route uses, and the entry point comes from the
 * contract's routing table. The screen resolves `entry.capability` against the
 * session and renders nothing of its own about satisfaction.
 */
export const workOrderRequirementsQuery = (workOrderId: string) =>
  queryOptions({
    queryKey: keys.workOrderRequirements(workOrderId),
    queryFn: () =>
      apiRequest<Envelope<WorkOrderRequirementsResponse>>(
        `/work-orders/${workOrderId}/requirements`,
      ),
  });

export function useWorkOrderRequirements(workOrderId: string | undefined) {
  return useQuery({
    ...workOrderRequirementsQuery(workOrderId ?? ""),
    enabled: Boolean(workOrderId),
  });
}

/* ── work stoppages ──────────────────────────────────────────────── */

/**
 * The timeline and the two clocks together.
 *
 * `refetchInterval` while a stoppage is open, and only then. `elapsedMinutes` is
 * computed server-side — the browser does no interval arithmetic here — so an open
 * stoppage's elapsed time is only as fresh as the last fetch. Polling a work order
 * with nothing running would be pointless traffic, so the interval is conditional
 * on the payload rather than constant.
 */
export const stoppagesQuery = (workOrderId: string) =>
  queryOptions({
    queryKey: keys.stoppages(workOrderId),
    queryFn: () => apiRequest<Envelope<StoppagesResponse>>(`/work-orders/${workOrderId}/stoppages`),
  });

export function useStoppages(workOrderId: string | undefined) {
  return useQuery({
    ...stoppagesQuery(workOrderId ?? ""),
    enabled: Boolean(workOrderId),
    refetchInterval: (query) => (query.state.data?.data.split.isPaused ? 60_000 : false),
  });
}

/**
 * What is attached to this work order, and the control that takes one off.
 *
 * Detached rows never appear: the route filters on `detached_at IS NULL`, which
 * is the same predicate `GET /documents/:id/content` uses to decide whether to
 * serve the bytes at all. One rule, one place — a list that showed a detached
 * document would offer a link that is 404 for everybody.
 *
 * **This list can come back SHORT — TRBUD-177.** A caller without
 * `viewFinancialDocuments` has invoice and receipt rows withheld by the route,
 * and `withheldFinancialCount` says how many. The envelope's `data` is therefore
 * an OBJECT rather than the bare array it used to be; the card renders the count,
 * because a silently short list reads as "nothing attached to this work order".
 * Same shape and same reason as `useAssetDocuments` below.
 */
export const workOrderDocumentsQuery = (workOrderId: string) =>
  queryOptions({
    queryKey: keys.workOrderDocuments(workOrderId),
    queryFn: () =>
      apiRequest<Envelope<WorkOrderAttachmentsResponse>>(
        `/work-orders/${workOrderId}/documents`,
      ),
  });

export function useWorkOrderDocuments(workOrderId: string | undefined) {
  return useQuery({
    ...workOrderDocumentsQuery(workOrderId ?? ""),
    enabled: Boolean(workOrderId),
  });
}

/**
 * Cancel a work order raised in error (TRBUD-95).
 *
 * Its own endpoint rather than a transition with a `toStageId`, and the API
 * refuses the cancelled stage through the generic transition route for exactly
 * that reason: the reason is required here and optional there, so one door is the
 * only way the requirement survives.
 */
export function useCancelWorkOrder(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: CancelWorkOrderBody) =>
      apiRequest<TransitionResult>(`/work-orders/${workOrderId}/cancel`, {
        method: "POST",
        body,
      }),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

/**
 * Attest that an attachment is the right receipt or invoice — TRBUD-97.
 *
 * Invalidates the work order as well as its documents: verifying is what
 * satisfies `builtin:receipt`, so the stage gates on the detail change with it.
 * Refetching only the document list would leave the screen still saying the work
 * order cannot advance.
 */
export function useVerifyDocument(workOrderId: string) {
  const client = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ attachmentId, body }: { attachmentId: string; body: VerifyDocumentBody }) =>
      apiRequest<{ attachmentId: string; documentId: string; verifiedAt: string | null }>(
        `/work-orders/${workOrderId}/documents/${attachmentId}/verify`,
        { method: "POST", body },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.workOrderDocuments(workOrderId) });
      invalidate.workOrder(workOrderId);
    },
    retry: false,
  });
}

export function useDetachDocument(workOrderId: string) {
  const client = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: { attachmentId: string; body: DetachDocumentBody }) =>
      apiRequest<{ attachmentId: string; documentId: string }>(
        `/work-orders/${workOrderId}/documents/${input.attachmentId}/detach`,
        { method: "POST", body: input.body },
      ),
    // The work order's own timeline drops the attachment too (migration `0043`),
    // so invalidating the list alone would leave the timeline claiming a document
    // nobody can open. `invalidate.workOrder` matches by key prefix and covers
    // both.
    onSettled: () => {
      invalidate.workOrder(workOrderId);
      void client.invalidateQueries({ queryKey: ["asset"] });
      // A detached invoice nobody entered leaves the Review queue (TRBUD-443).
      invalidateNavCounts(client);
    },
    retry: false,
  });
}

/* ── asset record: papers, photos and downtime corrections (TRBUD-151) ── */

/**
 * The papers filed against an asset — the read that made its detach reachable.
 *
 * `POST /assets/:id/documents/:attachmentId/detach` has existed since F-27 with
 * no caller anywhere in `apps/web`, and could not have had one: the detach is
 * keyed on `asset_document.id` and nothing handed a client that id. The asset
 * record returned `photos` (which are `asset_photo` rows) and no papers.
 *
 * Unlike the work-order list this one can come back SHORT: a caller without
 * `viewFinancialDocuments` has invoice rows withheld, and
 * `withheldFinancialCount` says how many. The card renders that count, because a
 * silently short list reads as "no paperwork on this unit".
 */
export function useAssetDocuments(assetId: string | undefined) {
  return useQuery({
    queryKey: keys.assetDocuments(assetId ?? ""),
    queryFn: () =>
      apiRequest<Envelope<AssetAttachmentsResponse>>(`/assets/${assetId}/documents`),
    enabled: Boolean(assetId),
  });
}

/**
 * Take a paper off an asset — TRBUD-151 item 4.
 *
 * A soft detach, exactly like the work-order side: `detached_at` is stamped and
 * the row survives for the audit trail. What goes away is REACHABILITY — every
 * arm of `GET /documents/:id/content` carries `detached_at IS NULL`, so the file
 * becomes 404 for everybody including the person who removed it.
 *
 * `invalidate.asset` rather than only the document list, because the asset
 * record's own payload is what the rest of the screen reads. Refetching the list
 * alone would be the same class of mistake `useDetachDocument` records for the
 * work-order timeline.
 */
export function useDetachAssetDocument(assetId: string) {
  const client = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: { attachmentId: string; body: DetachDocumentBody }) =>
      apiRequest<{ attachmentId: string; documentId: string }>(
        `/assets/${assetId}/documents/${input.attachmentId}/detach`,
        { method: "POST", body: input.body },
      ),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.assetDocuments(assetId) });
      invalidate.asset(assetId);
    },
    retry: false,
  });
}

/**
 * Delete a photograph of the wrong truck — TRBUD-151 item 6.
 *
 * ## Keyed on `documentId`, and that is not interchangeable
 *
 * `readAssetRecord` returns `document_id` for the photo strip and does NOT
 * return `asset_photo.id`, so the route is keyed on the document id and a hook
 * taking the photo row's id would produce a well-formed URL that 404s on every
 * photo for ever. `asset_photo_pair_uq` is unique on `(asset_id, document_id)`,
 * so it names exactly one row on this asset. The parameter is named
 * `documentId` rather than `id` for that reason.
 *
 * A HARD delete, unlike every other removal in the app: `asset_photo` is the one
 * table `tb_app` holds DELETE on. So the reason is still required even though the
 * row will not survive to carry it — the `audit_log` delta becomes the only place
 * the fact and its reason exist.
 */
export function useDeleteAssetPhoto(assetId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: { documentId: string; body: DetachDocumentBody }) =>
      apiRequest<{ photoId: string; documentId: string }>(
        `/assets/${assetId}/photos/${input.documentId}/delete`,
        { method: "POST", body: input.body },
      ),
    // The strip lives in the asset record's own payload rather than in a
    // separate query, so the record is the thing to invalidate.
    onSettled: () => invalidate.asset(assetId),
    retry: false,
  });
}

/**
 * Correct a downtime interval that was opened wrongly — TRBUD-151 item 3.
 *
 * `correct_asset_downtime` has existed since migration `0151` and the route since
 * F-22, with no caller anywhere in `apps/web`. Until then the eight fabricated
 * hours from a wrong mark-down were permanent: `tb_app` holds UPDATE on
 * `ended_at` alone and no DELETE, so the only remedy was a DBA statement outside
 * the audit trail.
 *
 * ## `invalidate.asset` is not optional here, and neither is the list
 *
 * A correction moves numbers on three screens at once — `downtime30dHours` on
 * this record and on the Assets list, the Reports chart, and the status board
 * (voiding an OPEN interval also puts the unit back in service). `invalidate.asset`
 * covers the record, the list and the board by prefix. `["reports"]` is added
 * explicitly because it is not under any of those, and a Reports tab left showing
 * the pre-correction hours is the same defect the correction exists to fix.
 *
 * `retry: false`, like every mutation here, and it matters more than usual: a
 * second correction of the same interval is refused (23514 → 422), so an
 * automatic retry after a lost reply would report "already corrected" for a call
 * that in fact succeeded.
 */
export function useCorrectDowntime(assetId: string) {
  const client = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: { downtimeId: string; body: CorrectDowntimeBody }) =>
      apiRequest<{
        downtimeId: string;
        supersededAt: string | null;
        replacementDowntimeId: string | null;
      }>(`/assets/${assetId}/downtime/${input.downtimeId}/correct`, {
        method: "POST",
        body: input.body,
      }),
    onSettled: () => {
      invalidate.asset(assetId);
      void client.invalidateQueries({ queryKey: ["reports"] });
      void client.invalidateQueries({ queryKey: keys.overview });
    },
    retry: false,
  });
}

/**
 * Take a document off a maintenance request — TRBUD-151 item 5.
 *
 * `request_document.detached_at` has existed since the column was added, is
 * granted to `tb_app`, and is READ by the content route's reachability predicate.
 * TRBUD-94 built detach for `work_order_document` only, so the privacy claim it
 * was raised for held for one parent out of three; F-27 built the route for this
 * one and nothing called it. A driver who photographs the wrong unit and files it
 * against a request had no control anywhere near it.
 *
 * A plain UPDATE server-side rather than a definer routine, which is why this
 * hook is thinner than `useDetachDocument`: `detach_work_order_document` exists
 * to carry two REFUSALS (a posted cost's evidence, and the last verified receipt
 * on a closed work order) and both are rules about work orders. Nothing gates on
 * a request attachment.
 *
 * Invalidates the inbox as well as the detail: `RequestInboxRow.photoCount` is a
 * `count(*)` over live `request_document` rows, so detaching one changes a number
 * on the list behind the panel. Refetching only the detail would leave the row
 * claiming a photo nobody can open.
 */
export function useDetachRequestDocument() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { requestId: string; attachmentId: string; body: DetachDocumentBody }) =>
      apiRequest<{ attachmentId: string; documentId: string }>(
        `/requests/${input.requestId}/documents/${input.attachmentId}/detach`,
        { method: "POST", body: input.body },
      ),
    onSettled: (_result, _error, variables) => {
      void client.invalidateQueries({ queryKey: keys.requestDetail(variables.requestId) });
      void client.invalidateQueries({ queryKey: ["request-inbox"] });
    },
    retry: false,
  });
}

export const stageTimeQuery = (workOrderId: string) =>
  queryOptions({
    queryKey: keys.stageTime(workOrderId),
    queryFn: () => apiRequest<Envelope<StageTimeResponse>>(`/work-orders/${workOrderId}/stage-time`),
  });

export function useStageTime(workOrderId: string | undefined) {
  return useQuery({
    ...stageTimeQuery(workOrderId ?? ""),
    enabled: Boolean(workOrderId),
    refetchInterval: 60_000,
  });
}

export function useStartStoppage(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: StartStoppageBody) =>
      apiRequest<StartStoppageReceipt & { reasonLabel: string }>(
        `/work-orders/${workOrderId}/stoppages`,
        { method: "POST", body },
      ),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

export function useResumeStoppage(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: { stoppageId: string; body: ResumeStoppageBody }) =>
      apiRequest<{ stoppageId: string; resumedAt: string }>(
        `/work-orders/${workOrderId}/stoppages/${input.stoppageId}/resume`,
        { method: "POST", body: input.body },
      ),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

/* ── Accounts ────────────────────────────────────────────────────────
 *
 * No `staleTime`. This list is short, read by one person at a time, and its
 * whole job is to answer "did that take effect" immediately after a change —
 * showing a cached role set straight after granting one is the single most
 * confusing thing this screen could do.
 */
export function useAdminUsers(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.adminUsers,
    queryFn: () => apiRequest<Envelope<AdminUserRow[]>>("/admin/users"),
    enabled: options.enabled ?? true,
  });
}

function useInvalidateAdminUsers() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: keys.adminUsers });
    // The signed-in person's own capabilities can change here — an admin can
    // demote themselves — and the session payload is what the shell renders
    // from, so it has to be re-read too.
    void client.invalidateQueries({ queryKey: keys.session });
  };
}

export function useCreateAdminUser() {
  const invalidate = useInvalidateAdminUsers();
  return useMutation({
    mutationFn: (body: AdminCreateUserBody) =>
      apiRequest<{ data: { userId: string; mustChangePassword: boolean } }>("/admin/users", {
        method: "POST",
        body,
      }),
    onSuccess: invalidate,
    // No retry. Every failure here is a refusal to read — a duplicate address,
    // a rejected role set — and re-sending changes nothing except how many
    // times it is logged. A retried CREATE is also the one that could produce a
    // second account if the first actually succeeded.
    retry: false,
  });
}

/**
 * Invite someone by email: create the account and mail them a link to set it up.
 *
 * The result carries how the email went AND the link, because when email is off or
 * refused the administrator is the one who hands it over. Never retried, for the
 * reason `useCreateAdminUser` gives: a retried create is the one that could make a
 * second account if the first actually succeeded.
 */
export function useInviteAdminUser() {
  const invalidate = useInvalidateAdminUsers();
  return useMutation({
    mutationFn: (body: AdminInviteUserBody) =>
      apiRequest<{ data: AdminInviteResult }>("/admin/users/invite", { method: "POST", body }),
    onSuccess: invalidate,
    retry: false,
  });
}

/** Send an unaccepted person a new link; the earlier one stops working. */
export function useResendInvite(userId: string) {
  const invalidate = useInvalidateAdminUsers();
  return useMutation({
    mutationFn: () =>
      apiRequest<{ data: AdminInviteResult }>(`/admin/users/${userId}/resend-invite`, {
        method: "POST",
        body: {},
      }),
    onSuccess: invalidate,
    retry: false,
  });
}

export function useSetUserAccess(userId: string) {
  const invalidate = useInvalidateAdminUsers();
  return useMutation({
    mutationFn: (body: AdminSetUserAccessBody) =>
      apiRequest<{ data: { ok: boolean } }>(`/admin/users/${userId}/access`, {
        method: "PATCH",
        body,
      }),
    onSuccess: invalidate,
    retry: false,
  });
}

export function useResetUserPassword(userId: string) {
  const invalidate = useInvalidateAdminUsers();
  return useMutation({
    mutationFn: (body: { temporaryPassword: string }) =>
      apiRequest<{ data: { ok: boolean; sessionsRevoked: boolean } }>(
        `/admin/users/${userId}/reset-password`,
        { method: "POST", body },
      ),
    onSuccess: invalidate,
    retry: false,
  });
}

/**
 * The collector switchboard.
 *
 * `staleTime: 0` for the same reason the permission switches use it, and more
 * sharply: the likely reader is somebody mid-incident who wants to know whether
 * a collector is already off. A cached "on" from five minutes ago is the wrong
 * answer to ask them to act on.
 */
export function useCollectorSettings(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.collectorSettings,
    queryFn: () => apiRequest<CollectorSettingsResponse>("/settings/collectors"),
    enabled: options.enabled ?? true,
    staleTime: 0,
  });
}

/**
 * Collector health, fetched separately from the switchboard.
 *
 * A separate query so a health failure cannot blank the switches — the controls
 * are why somebody opened the page, and they must render even when this does not.
 * `staleTime: 0` for the same reason the board uses it: this is a
 * "what is happening right now" panel, and a cached answer is the wrong answer.
 */
export function useCollectorHealth(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.collectorHealth,
    queryFn: () => apiRequest<CollectorHealthResponse>("/settings/collectors/health"),
    enabled: options.enabled ?? true,
    staleTime: 0,
  });
}

/**
 * Who is valued at what — TRBUD-93 / TICKET-815.
 *
 * `staleTime: 0` because a rate somebody just set elsewhere changes what every
 * later time entry costs, and a cached "no rate" is the exact state this screen
 * exists to clear.
 */
export function useLabourRates(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.labourRates,
    queryFn: () => apiRequest<LabourRatesResponse>("/labour-rates"),
    enabled: options.enabled ?? true,
    staleTime: 0,
  });
}

/**
 * Whether anything is scanning uploaded documents.
 *
 * `staleTime: 0` for the same reason the collector health panel uses it — this
 * is a "what is happening right now" reading, and a cached answer is the wrong
 * answer on a page somebody opened because a receipt would not open.
 */
export function useDocumentScanHealth(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.documentScanHealth,
    queryFn: () => apiRequest<DocumentScanHealthResponse>("/settings/documents/scan-health"),
    enabled: options.enabled ?? true,
    staleTime: 0,
  });
}

/**
 * Set one person's rate.
 *
 * Invalidated rather than written into the cache: unlike the collector board the
 * response is a receipt for ONE row, not the new table, and rates VERSION rather
 * than overwrite — so the authoritative answer to "what is their rate now" is
 * the server's, after the new period has opened.
 */
export function useSetLabourRate(userId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: SetLabourRateBody) =>
      apiRequest<{ labourRateId: string }>(`/labour-rates/${userId}`, { method: "PUT", body }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.labourRates }),
    retry: false,
  });
}

/**
 * Ask for one document to be looked at again.
 *
 * This queues a request; it cannot mark anything clean. The panel is re-fetched
 * rather than patched in place because the server decides whether the request
 * was even legal — a quarantined document is refused — and guessing the new
 * state here would show a retry that did not happen.
 */
export function useRequestRescan() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) =>
      apiRequest<{ documentId: string; queued: true }>(`/documents/${documentId}/rescan`, {
        method: "POST",
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.documentScanHealth }),
  });
}

export function useSaveCollectorSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: SetCollectorSettingsBody) =>
      apiRequest<CollectorSettingsResponse>("/settings/collectors", { method: "PATCH", body }),
    // The response IS the new board, so it is written straight into the cache.
    // Re-fetching would work too, but it briefly shows the old version number —
    // which is the one the next save would send, and it would be refused.
    onSuccess: (saved) => client.setQueryData(keys.collectorSettings, saved),
    // No retry. A 409 means somebody else saved first; re-sending the same stale
    // version would be refused again, identically.
    retry: false,
  });
}

// ── The home base mapping (TRBUD-171) ────────────────────────────────
//
// `enabled` matters here for the same reason it does on the other /config
// hooks: the endpoint is `configuration`-gated, and firing it for a mechanic
// renders a 403 beside panels that loaded fine.

export function useConfigHomeBaseMap(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.configHomeBaseMap,
    queryFn: () => apiRequest<Envelope<HomeBaseMapAdminRow[]>>("/config/home-base-map"),
    enabled: options.enabled ?? true,
  });
}

function useInvalidateHomeBaseMap() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: keys.configHomeBaseMap });
  };
}

export function useCreateHomeBaseMap() {
  const invalidate = useInvalidateHomeBaseMap();
  return useMutation({
    mutationFn: (body: CreateHomeBaseMapBody) =>
      apiRequest<HomeBaseMapReceipt>("/config/home-base-map", { method: "POST", body }),
    onSettled: invalidate,
    retry: false,
  });
}

export function useUpdateHomeBaseMap(homeBaseMapId: string) {
  const invalidate = useInvalidateHomeBaseMap();
  return useMutation({
    mutationFn: (body: UpdateHomeBaseMapBody) =>
      apiRequest<HomeBaseMapReceipt>(`/config/home-base-map/${homeBaseMapId}`, {
        method: "PATCH",
        body,
      }),
    onSettled: invalidate,
    retry: false,
  });
}

/**
 * Set a catalogue part's unit cost — `PATCH /parts/:id`. TRBUD-209.
 *
 * `invalidate.everything()` rather than just `["parts"]`, and the breadth is the
 * point rather than laziness. A part's price is read in more places than the
 * Parts list: the work-order Issue-part picker decides whether its submit is
 * enabled from `unitCost`, the stock valuation on the Parts screen derives
 * `extendedValue` from it, and the asset record's cost rollups sit downstream of
 * both. A narrower invalidation would leave the Issue control still refusing a
 * part whose price had just been set on the screen behind it — which is the
 * TRBUD-209 dead end rebuilt one layer up, out of a stale cache instead of a
 * missing route.
 */
export function useSetPartUnitCost(partId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: SetPartUnitCostBody) =>
      apiRequest<{
        partId: string;
        partNumber: string;
        unitCost: string;
        previousUnitCost: string | null;
      }>(`/parts/${partId}`, { method: "PATCH", body }),
    onSuccess: () => invalidate.everything(),
    retry: false,
  });
}

/**
 * Record a later manual load-status observation — DEC-01.
 *
 * `invalidate.workOrder` and nothing else. The queue's chip reads
 * `loadStatusAtOpen`, which this cannot change: the toggle appends to
 * `work_order_load_status_event` and the stamp is unwritable by the app login,
 * so invalidating the queue would refetch every row to redraw an identical chip.
 * The detail screen is the only reader whose answer moves.
 */
export function useRecordLoadStatus(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: RecordWorkOrderLoadStatusBody) =>
      apiRequest<{ workOrderId: string; eventId: string; changedAt: string }>(
        `/work-orders/${workOrderId}/load-status`,
        { method: "POST", body },
      ),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

/** Save one vendor's service warranty, then refresh the detail and queue summary. */
export function useSetWorkOrderVendorWarranty(workOrderId: string, vendorId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: SetWorkOrderVendorWarrantyBody) =>
      apiRequest<SetWorkOrderVendorWarrantyReceipt>(`/work-orders/${workOrderId}/vendors/${vendorId}/warranty`, {
        method: "PUT",
        body,
      }),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

/**
 * The custom-field values on a work order — TRBUD-249.
 *
 * The body carries the WHOLE object, not the field that changed:
 * `tb_validate_custom_fields` judges `NEW.custom_fields` entire, including
 * "every active required field is present", so a partial body would be asking
 * the database a question about an object it was never shown. The card composes
 * it from every field of the work order's own schema, including the retired ones
 * it renders read-only — dropping one of those from the payload would delete a
 * recorded value silently, because the trigger only refuses a CHANGE to an
 * inactive field and says nothing about an absent key.
 */
export function useSetWorkOrderCustomFields(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: SetWorkOrderCustomFieldsBody) =>
      apiRequest<SetWorkOrderCustomFieldsReceipt>(`/work-orders/${workOrderId}/custom-fields`, {
        method: "PATCH",
        body,
      }),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

/**
 * Close ONE MaintainX-imported work order the old system already finished —
 * TRBUD-244.
 *
 * `invalidate.workOrder` refreshes the detail AND the queue, which matters more
 * here than for a field edit: the work order changes stage, so the row moves
 * within the queue and its own stage stepper has to redraw.
 */
export function useCloseImportedWorkOrder(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: CloseImportedWorkOrderBody) =>
      apiRequest<CloseImportedWorkOrderResult>(`/work-orders/${workOrderId}/close-imported`, {
        method: "POST",
        body,
      }),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

/**
 * The bulk sweep — TRBUD-244. ONE hook for both the dry run and the apply.
 *
 * Deliberately not two hooks: the confirmation shows the report's counts and then
 * has to show what actually happened, the response shape is identical, and two
 * caches of the same numbers can disagree. The caller passes `apply`.
 *
 * `retry: false` matters more than usual here. The apply is idempotent in the
 * database — an already-closed work order answers `alreadyClosed` rather than
 * moving again — but a silent retry would re-run a sweep the operator confirmed
 * once, and the honest response to a failed bulk write is to show the error and
 * let them look before pressing it again.
 */
export function useCloseImportedWorkOrders() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: CloseImportedWorkOrdersBody) =>
      apiRequest<CloseImportedBulkResult>("/work-orders/close-imported", {
        method: "POST",
        body,
      }),
    onSettled: (_data, _error, variables) => {
      // Only an APPLY changed anything. Invalidating after a dry run would throw
      // away a good queue cache to learn nothing.
      if (variables?.apply) invalidate.everything();
    },
    retry: false,
  });
}

/**
 * Enter or leave a role preview — TRBUD-253.
 *
 * A FULL RELOAD on success, not a cache invalidation, and for the same reason the
 * dev persona picker reloads: the preview changes which routes exist, which
 * default screen applies and which rows every list may return. Re-deriving all of
 * that in place is a lot of machinery, and half-derived is worse than reloaded —
 * a screen the preview may no longer open would sit there rendering stale rows
 * while the API refused every refetch.
 *
 * `window.location.assign("/")` rather than `reload()`, because the screen you
 * were on may be one the previewed role cannot open; landing on `/` sends the
 * shell to that role's own default route.
 */
export function useSetPreviewRole() {
  return useMutation({
    mutationFn: (body: SetPreviewRoleBody) =>
      apiRequest<SessionResponse>("/session/preview-role", { method: "POST", body }),
    onSuccess: () => window.location.assign("/"),
    retry: false,
  });
}

/**
 * Place an unclassified asset by hand — `PATCH /assets/:id/class`, plan 2.1.
 *
 * `invalidate.everything()` rather than `invalidate.asset(id)`: the class is a
 * FILTER on the assets list and on the work-order asset picker, so a unit that
 * has just been classified has to leave the Unclassified tab and appear under its
 * new one. Invalidating the single asset would update the record page and leave
 * every list showing the row where it no longer belongs.
 */
export function useSetAssetClass(assetId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: SetAssetClassBody) =>
      apiRequest<AssetClassReceipt>(`/assets/${assetId}/class`, { method: "PATCH", body }),
    onSettled: () => invalidate.everything(),
    retry: false,
  });
}

/**
 * The vocabularies the edit-asset dialog's dropdowns are drawn from —
 * `GET /assets/field-options`.
 *
 * `enabled` so the dialog can fetch on OPEN rather than on every mount of the
 * asset record: this is a fleet-wide read that only the dialog uses, and the
 * record page is opened far more often than the dialog is. Fetching it with the
 * record would be a request per page view for a list nobody is looking at.
 *
 * Not keyed on the asset. The lists are the fleet's, so one cache entry serves
 * however many units a person corrects in a sitting — and correcting one unit's
 * make can add a row to `asset_make`, which is why the profile mutation below
 * invalidates this key rather than leaving the next dialog with a list missing
 * the make just created.
 */
export function useAssetFieldOptions(enabled: boolean) {
  return useQuery({
    queryKey: ["asset-field-options"],
    queryFn: () => apiRequest<Envelope<AssetFieldOptions>>("/assets/field-options"),
    enabled,
  });
}

/**
 * Correct what a vendor got wrong — `PATCH /assets/:id/profile`.
 *
 * `invalidate.everything()`, matching `useSetAssetClass` directly above and for
 * the same reason rather than by imitation: `assetType` is a FILTER on the
 * register and the make and model are searched, so a unit whose type has just
 * been corrected has to move between filter groups. Invalidating the single
 * asset would fix the record page and leave every list showing the old value.
 *
 * The options key goes with it, because saving a make nobody has bought before
 * REGISTERS it in `asset_make` through the 0061 trigger — so the list this hook
 * feeds is stale the moment the save returns, and the next dialog would offer a
 * vocabulary missing the row it had just created.
 */
export function useSetAssetProfile(assetId: string) {
  const invalidate = useInvalidate();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: AssetProfileBody) =>
      apiRequest<AssetProfileReceipt>(`/assets/${assetId}/profile`, { method: "PATCH", body }),
    onSettled: async () => {
      await client.invalidateQueries({ queryKey: ["asset-field-options"] });
      invalidate.everything();
    },
    retry: false,
  });
}

/* ── Work-order itemization: issues and packages ─────────────────────────
 *
 * Three writers behind the visit screen. All three settle by sweeping the
 * work order, which `invalidate.workOrder` already widens to the queue, board,
 * overview and reports — and a new package IS a new queue row, so that width
 * is the point rather than a cost.
 */

export function useAddWorkOrderIssues(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: AddWorkOrderIssuesBody) =>
      apiRequest<{ issueIds: string[]; visitWorkOrderId: string }>(
        `/work-orders/${workOrderId}/issues`,
        { method: "POST", body },
      ),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

export function useUpdateWorkOrderIssue(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: { issueId: string; body: UpdateWorkOrderIssueBody }) =>
      apiRequest<{ issueId: string; status: string; packageWorkOrderId: string | null }>(
        `/work-orders/${workOrderId}/issues/${input.issueId}`,
        { method: "PATCH", body: input.body },
      ),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

export function useCreateWorkPackage(visitWorkOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: CreateWorkPackageBody) =>
      apiRequest<CreateWorkPackageResult>(`/work-orders/${visitWorkOrderId}/packages`, {
        method: "POST",
        body,
      }),
    onSettled: () => invalidate.workOrder(visitWorkOrderId),
    retry: false,
  });
}

/* ── The Repair overview board's manager actions — 18 September 2026 ──
 *
 * Eden asked for the board to be somewhere a manager ACTS rather than only
 * reads: raise a card's priority, flag one for the mechanic, and do either to a
 * handful at once. Three writes, and every one of them invalidates through
 * `invalidate.workOrder`, which already sweeps `["queue"]` — the key both
 * `useKanbanBoard` and `useKanbanCell` sit under — plus the board, the overview
 * and the reports. So a card that changed priority moves column on the next
 * answer from the server rather than because the screen decided it had.
 */

/**
 * `PATCH /work-orders/:id/priority`.
 *
 * Gated on `editDueDate`, which is the capability the due date already takes and
 * the reason `/warranty` records for reusing it: a manager's scheduling levers
 * are one permission, and inventing a `setPriority` capability would be a second
 * place for the grant surface to drift from the route's.
 */
export function useSetWorkOrderPriority(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: SetWorkOrderPriorityBody) =>
      apiRequest<{ priority: Priority }>(`/work-orders/${workOrderId}/priority`, {
        method: "PATCH",
        body,
      }),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

/**
 * `PATCH /work-orders/:id/title` — rename a work order (TRBUD-311).
 *
 * On settle the work order, queue, kanban, Gantt, board and reports refresh
 * through `invalidate.workOrder`, and the asset record is refreshed too: it lists
 * the asset's work orders by title and is otherwise stale for 30 seconds.
 */
export function useSetWorkOrderTitle(workOrderId: string, assetId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: SetWorkOrderTitleBody) =>
      apiRequest<{ title: string }>(`/work-orders/${workOrderId}/title`, {
        method: "PATCH",
        body,
      }),
    onSettled: () => {
      invalidate.workOrder(workOrderId);
      invalidate.asset(assetId);
    },
    retry: false,
  });
}

/**
 * `PUT /work-orders/:id/roadside` — correct the Roadside breakdown mark
 * (TRBUD-409). `invalidate.workOrder` refreshes the job, the queue and the
 * reports, so the Roadside panel follows the correction.
 */
export function useSetWorkOrderRoadside(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: import("@truckbuddy/contract").SetWorkOrderRoadsideBody) =>
      apiRequest<import("@truckbuddy/contract").SetWorkOrderRoadsideReceipt>(`/work-orders/${workOrderId}/roadside`, {
        method: "PUT",
        body,
      }),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

/** `POST /work-orders/:id/attention` — raise the flag, with an optional note. */
export function useFlagWorkOrderAttention(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: FlagWorkOrderAttentionBody) =>
      apiRequest<QueueAttention>(`/work-orders/${workOrderId}/attention`, {
        method: "POST",
        body,
      }),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

/**
 * Lower the manager's needs-attention flag — TRBUD board views, 18 September 2026.
 *
 * ── ONE press, and that is deliberate ────────────────────────────────
 *
 * The house rule is two presses for an irreversible write. Clearing a flag is
 * not one: the state it removes can be put back by pressing Flag again, no
 * ledger is touched, no stage moves, and the audit trail keeps both acts. A
 * confirmation dialog here would be ceremony on the cheapest action on the
 * screen — and the expensive act (raising the flag, which a mechanic then acts
 * on) is the one the board makes deliberate.
 *
 * `invalidate.workOrder` rather than a narrower key, because the flag is
 * rendered in four places fed by three caches: the work-order record, the queue
 * (both rows), the kanban board and the overview. That helper already
 * invalidates all of them, which is why a route as small as this needs no
 * bespoke invalidation.
 *
 * No retry. A failure here is a refusal — a role that may not edit, or a work
 * order that is gone — and re-sending changes nothing but the log.
 */
export function useClearWorkOrderAttention(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: ClearWorkOrderAttentionBody) =>
      apiRequest<{ attention: null }>(`/work-orders/${workOrderId}/attention/clear`, {
        method: "POST",
        body,
      }),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

/**
 * `POST /work-orders/:id/no-parts-used` — record that an in-house job took no
 * parts (TRBUD-300). `invalidate.workOrder` is the whole refresh: the detail's
 * `noPartsUsed`, the Timeline row and the requirements panel (a prefix key under
 * the work order) all read the two columns this writes.
 *
 * No retry, for the attention flag's reason: a failure is a refusal (a part is
 * issued, the work order closed) and re-sending changes nothing but the log.
 */
export function useMarkNoPartsUsed(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: MarkNoPartsUsedBody) =>
      apiRequest<NoPartsUsedReceipt>(`/work-orders/${workOrderId}/no-parts-used`, {
        method: "POST",
        body,
      }),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

/** `POST /work-orders/:id/no-parts-used/clear` — withdraw the choice before close. */
export function useClearNoPartsUsed(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: ClearNoPartsUsedBody) =>
      apiRequest<NoPartsUsedReceipt>(`/work-orders/${workOrderId}/no-parts-used/clear`, {
        method: "POST",
        body,
      }),
    onSettled: () => invalidate.workOrder(workOrderId),
    retry: false,
  });
}

/** One action applied to a selection — the discriminant the bulk bar sends. */
export type BulkWorkOrderAction =
  | { kind: "priority"; priority: Priority }
  | { kind: "flag"; note?: string }
  | { kind: "assign"; userId: string | null; vendorContactId: string | null };

/** What happened to ONE work order in a batch. `error` is null on success. */
export interface BulkWorkOrderOutcome {
  workOrderId: string;
  error: string | null;
}

/**
 * The same write applied to every selected card, one request at a time.
 *
 * ── It does NOT abort on the first failure, and that is the whole design ──
 * A batch that stops at the third of eight leaves the manager with no idea which
 * of the eight were written, and the obvious recovery — press it again — re-sends
 * the two that already succeeded. So every id is attempted, each carries its OWN
 * idempotency key (they are separate audited commands, and one shared key would
 * make the second write read as a replay of the first and silently do nothing),
 * and the result is one outcome per work order for the dialog to print by id.
 *
 * ── Sequential rather than `Promise.all` ──
 * These are audited writes against rows a person is looking at. Eight at once is
 * eight transactions racing for one cache invalidation and, on a slow link,
 * eight chances to half-finish in an order nobody can reconstruct afterwards.
 * One at a time is slower and is reportable.
 *
 * A failure is CAUGHT rather than thrown, so the mutation resolves with the
 * mixed result and the dialog can render it — rejecting would put the whole
 * batch in `error` and lose the record of which ones succeeded.
 */
export function useBulkWorkOrderAction() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({
      workOrderIds,
      action,
    }: {
      workOrderIds: readonly string[];
      action: BulkWorkOrderAction;
    }): Promise<BulkWorkOrderOutcome[]> => {
      const outcomes: BulkWorkOrderOutcome[] = [];
      for (const workOrderId of workOrderIds) {
        try {
          if (action.kind === "priority") {
            await apiRequest(`/work-orders/${workOrderId}/priority`, {
              method: "PATCH",
              body: { priority: action.priority, idempotencyKey: newIdempotencyKey("priority") },
            });
          } else if (action.kind === "flag") {
            await apiRequest(`/work-orders/${workOrderId}/attention`, {
              method: "POST",
              body: {
                // Spread rather than `note: action.note`: the body is a
                // `strictObject` and the note is `.optional()`, so "no note" has
                // to be an ABSENT key and an explicit `undefined` would be a
                // different thing to serialise.
                ...(action.note === undefined ? {} : { note: action.note }),
                idempotencyKey: newIdempotencyKey("attention"),
              },
            });
          } else {
            await apiRequest(`/work-orders/${workOrderId}/assignments`, {
              method: "POST",
              body: {
                userId: action.userId,
                vendorContactId: action.vendorContactId,
                idempotencyKey: newIdempotencyKey("assign"),
              },
            });
          }
          outcomes.push({ workOrderId, error: null });
        } catch (error) {
          outcomes.push({
            workOrderId,
            error: error instanceof Error ? error.message : "The write failed.",
          });
        }
      }
      return outcomes;
    },
    /*
     * ONE sweep at the end, not one per id. `invalidate.workOrder` invalidates
     * the whole `["queue"]` key rather than a single work order's, so calling it
     * eight times would refetch the board eight times for one batch. The id
     * passed is only the one whose own detail key is swept; the list keys that
     * the board actually reads are swept for all of them either way.
     */
    onSettled: (_result, _error, variables) => {
      const first = variables.workOrderIds[0];
      if (first !== undefined) invalidate.workOrder(first);
    },
    retry: false,
  });
}

/* ── part summary and photos ──────────────────────────────────────── */

/** The Parts side panel: price, stock per shelf, where bought, photos. */
export function usePartSummary(partId: string | undefined) {
  return useQuery({
    queryKey: ["parts", partId ?? "", "summary"] as const,
    queryFn: () => apiRequest<Envelope<PartSummary>>(`/parts/${partId}/summary`),
    enabled: partId !== undefined,
  });
}

function useInvalidatePart(partId: string) {
  const client = useQueryClient();
  return () => void client.invalidateQueries({ queryKey: ["parts", partId] });
}

export function useDeletePartPhoto(partId: string) {
  const invalidatePart = useInvalidatePart(partId);
  return useMutation({
    mutationFn: (input: { documentId: string; body: DetachDocumentBody }) =>
      apiRequest<{ photoId: string; documentId: string }>(
        `/parts/${partId}/photos/${input.documentId}/delete`,
        { method: "POST", body: input.body },
      ),
    onSettled: invalidatePart,
    retry: false,
  });
}

export function useSetPrimaryPartPhoto(partId: string) {
  const invalidatePart = useInvalidatePart(partId);
  return useMutation({
    mutationFn: (documentId: string) =>
      apiRequest<{ documentId: string }>(`/parts/${partId}/photos/${documentId}/primary`, {
        method: "POST",
        body: {},
      }),
    onSettled: invalidatePart,
    retry: false,
  });
}

/* ── profile photo ────────────────────────────────────────────────── */

export function useSetAvatar() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (dataUrl: string) =>
      apiRequest<{ avatarVersion: string | null }>("/me/avatar", { method: "PUT", body: { dataUrl } }),
    onSettled: () => void client.invalidateQueries({ queryKey: keys.session }),
    retry: false,
  });
}

export function useRemoveAvatar() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<{ avatarVersion: null }>("/me/avatar/remove", { method: "POST", body: {} }),
    onSettled: () => void client.invalidateQueries({ queryKey: keys.session }),
    retry: false,
  });
}

export function useSetPartWarranty(partId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: SetPartWarrantyBody) => apiRequest<{ partId: string; covered: boolean; note: string | null }>(
      `/parts/${partId}/warranty`, { method: "PUT", body },
    ),
    onSettled: () => invalidate.everything(),
    retry: false,
  });
}

export function useSetPartProductUrl(partId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: SetPartProductUrlBody) => apiRequest<{ partId: string; productUrl: string | null }>(
      `/parts/${partId}/product-url`, { method: "PUT", body },
    ),
    onSuccess: () => client.invalidateQueries({ queryKey: ["parts", partId] }),
    retry: false,
  });
}

export function useMainxExpenseReview(query: import("@truckbuddy/contract").MainxExpenseReviewQuery) {
  // The previous tab's lines and counts stay on screen while the next tab loads,
  // so the tab counts do not blank on every click.
  return useQuery({ queryKey: ["mainx-expense-review", query], queryFn: () => apiRequest<import("@truckbuddy/contract").MainxExpenseReviewResponse>("/reports/mainx-review", { query }), placeholderData: keepPreviousData });
}
export function useMainxExpenseEvidence(id: string) {
  return useQuery({ queryKey: ["mainx-expense-evidence", id], queryFn: () => apiRequest<{ data: import("@truckbuddy/contract").MainxExpenseReviewDetail }>(`/reports/mainx-review/${id}`) });
}
export function useSaveMainxExpenseDecision(id: string) {
  const client = useQueryClient();
  return useMutation({ mutationFn: (body: import("@truckbuddy/contract").MainxExpenseDecisionBody) => apiRequest<{ decisionId: string }>(`/reports/mainx-review/${id}`, { method: "POST", body }),
    onSettled: () => { for (const key of ["mainx-expense-review", "mainx-expense-evidence", "reports", "vendors", "vendor", "work-order"]) void client.invalidateQueries({ queryKey: [key] }); }, retry: false });
}

/* ── TRBUD-416: parts handed to a mechanic ───────────────────────────────
 *
 * Every write here changes stock and, for a named hand-over or an apply, a
 * job's cost, so each invalidates everything, as the other stock mutations do.
 */

/** The signed-in person's parts. `enabled` is `can.applyIssuedParts`. */
export const myIssuedPartsQuery = queryOptions({
  queryKey: ["me", "issued-parts"] as const,
  queryFn: () =>
    apiRequest<import("@truckbuddy/contract").Envelope<import("@truckbuddy/contract").MyIssuedParts>>(
      "/me/issued-parts",
    ),
});

export function useMyIssuedParts(enabled: boolean) {
  return useQuery({ ...myIssuedPartsQuery, enabled });
}

/** Who parts may be handed to. `enabled` is the dialog being open. */
export function useCustodyHolders(enabled: boolean) {
  return useQuery({
    queryKey: ["parts", "custody-holders"] as const,
    queryFn: () =>
      apiRequest<
        import("@truckbuddy/contract").Envelope<import("@truckbuddy/contract").CustodyHolderOption[]>
      >("/parts/custody-holders"),
    enabled,
    staleTime: 60 * 1000,
  });
}

export function useIssuePartToMechanic() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: import("@truckbuddy/contract").IssuePartToMechanicBody) =>
      apiRequest<import("@truckbuddy/contract").IssueToMechanicReceipt>("/parts/issue-to-mechanic", {
        method: "POST",
        body,
      }),
    onSuccess: () => invalidate.everything(),
    retry: false,
  });
}

export function useApplyIssuedPart(workOrderId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: import("@truckbuddy/contract").ApplyIssuedPartBody) =>
      apiRequest<import("@truckbuddy/contract").ApplyIssuedPartReceipt>(
        `/work-orders/${workOrderId}/apply-issued-part`,
        { method: "POST", body },
      ),
    onSuccess: () => invalidate.everything(),
    retry: false,
  });
}

export function useReturnPartFromCustody() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: import("@truckbuddy/contract").ReturnPartFromCustodyBody) =>
      apiRequest<import("@truckbuddy/contract").CustodyReturnReceipt>("/parts/custody/return", {
        method: "POST",
        body,
      }),
    onSuccess: () => invalidate.everything(),
    retry: false,
  });
}

/* ── TRBUD-406: terminal spare-stock coverage ───────────────────────────
 *
 * Keyed under `["parts", ...]` on purpose: every stock movement already
 * invalidates the parts list by that prefix (or invalidates everything), so the
 * coverage follows a receipt, an issue or a hand-over without each of those
 * mutations having to know this panel exists.
 */
const spareStockKey = ["parts", "spare-stock"] as const;

export function useSpareStock() {
  return useQuery({
    queryKey: spareStockKey,
    queryFn: () =>
      apiRequest<import("@truckbuddy/contract").Envelope<import("@truckbuddy/contract").SpareStockResponse>>(
        "/parts/spare-stock",
      ),
  });
}

export function useCreateSpareStockRule() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: import("@truckbuddy/contract").CreateSpareStockRuleBody) =>
      apiRequest<{ data: import("@truckbuddy/contract").SpareStockRuleReceipt }>("/parts/spare-stock-rules", {
        method: "POST",
        body,
      }),
    onSettled: () => client.invalidateQueries({ queryKey: spareStockKey }),
    retry: false,
  });
}

export function useUpdateSpareStockRule(ruleId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: import("@truckbuddy/contract").UpdateSpareStockRuleBody) =>
      apiRequest<{ data: import("@truckbuddy/contract").SpareStockRuleReceipt }>(
        `/parts/spare-stock-rules/${ruleId}`,
        { method: "PATCH", body },
      ),
    onSettled: () => client.invalidateQueries({ queryKey: spareStockKey }),
    retry: false,
  });
}
